import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import { Fragment, useMemo, useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Panel } from "@/components/Panel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ApiError, isTransportError } from "@/api/client";
import {
  usePurchaseOrder, usePatchPurchaseOrder, useSetPurchaseOrderStatus,
  useCreatePurchaseShipment, usePatchPurchaseShipment,
  useDeletePurchaseShipment, useSetShipmentItemCartonException, useArchivePurchaseOrder, useRestorePurchaseOrder,
  usePurchasePurgePreview, usePurgePurchaseOrder,
  useDeletePurchasePayment,
  usePatchPurchaseCost,
  usePurchaseDocuments, useUploadDocument, useDocumentReview, useConfirmDocument, useRejectDocument, useDeleteDocument, useReopenDocument,
  useAssignDocument, useAblegenDocument,
  useConfirmShipmentDelivery,
  useStartmengeKandidaten, usePushStartmenge, useEinlagerung, usePushFinal,
  useMischEkVorschau, useConfirmMischEk,
  type PushResultItem, type ConfirmMischEkResultItem, type PurchaseOrderFile,
  type PurchaseShipment, type ConfirmDocumentMapping,
  type ConfirmDocumentAggregation, type ReviewAggregation, type ReviewDiffRow, type DocumentReviewResponse,
  type PurchasePurgeReasonCode,
  bestellungPdfHerunterladen,
  lieferantenPdfHerunterladen,
  dokumentHerunterladen,
} from "@/api/purchase";
import {
  formatUsd, orderStatusLabel, shipmentStatusLabel, paymentStatusLabel,
  docStatusLabel, docTypLabel, weStatusLabel, weStatusBadgeClass, dokumentLoeschHinweis,
  pushErgebnisLabel, mischEkHinweis,
  uploadZusammenfassung, uploadOhneAuswertungText, type UploadBilanz,
  ORDER_STATUS_OPTIONS,
} from "@/lib/purchase-format";
import { formatEuro } from "@/lib/dash-format";
import { useSuppliers } from "@/api/dashboard";
import { bestellungAlsCsv, csvDateiname, csvHerunterladen } from "./csv";
import { zustellErgebnisText } from "./board/zeilen";
import { ContainerAbgleichFeld, normalisiereContainerNr } from "./ContainerAbgleich";
import { KostenSollAktionen, KostenSollHinweis, NeuerKostenposten, kostenPatchBody } from "./KostenSoll";
import { NeueZahlung, RechnungssummeFeld, ZahlungAnrechnung, ZahlungsKarte } from "./ZahlungFormulare";
import { formatEuroGenau } from "./board/InlineZellen";
import { einheitZusatz, jeEinheit, mengeMitEinheit } from "./einheit";
import { fehlerText, istTransportmeldung } from "@/lib/fehlerText";
import { PositionenHinzufuegenDialog } from "./PositionenHinzufuegenDialog";
import { AnforderungAnGaKnopf, PositionLoeschenKnopf, PositionNotiz } from "./PositionAktionen";
import { OffenZelle, UnzugeordnetHinweis, VerschifftZelle, containerKennungen } from "./Restmengen";
import { MengenTabelle } from "./MengenTabelle";
import { PackstationAnteil } from "./PackstationAnteil";
import { useAuth } from "@/api/auth";
import { downloadPdf } from "@/api/warehouse";
import { PinConfirmDialog } from "@/components/purchase/PinConfirmDialog";
import { getShipmentWorkflow, ShipmentWorkflowStepper } from "./workflow/ShipmentWorkflowStepper";
import { Arbeitsplatz } from "./arbeitsplatz/Arbeitsplatz";
import { ContainerStatusAktionen } from "./ContainerStatusAktionen";
import { BestandBuchenDialog } from "./BestandBuchenDialog";
import { AMPEL_KLASSE, AMPEL_TEXT, altBestaetigtBody, altPflichtOffen, grundText, herkunftText, mischEkLokal } from "./bestand-buchen";
import { AKTE_REITER, type AkteReiter } from "./arbeitsplatz/AufgabeKarte";

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" }) : "—";


/** Plan 2026-10-16: Packstation-Liste des verknuepften WE (welche Kartons sofort in die Packstation). */
function PackstationListeKnopf({ receiptId }: { receiptId: number }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button size="sm" variant="outline" disabled={busy}
      onClick={async () => {
        setBusy(true);
        try { await downloadPdf(`/warehouse/receipt/${receiptId}/packstation-liste-pdf/`, `packstation-liste-we-${receiptId}.pdf`); }
        catch (e) { toast.error(fehlerText(e, "Packstation-Liste konnte nicht geladen werden")); }
        finally { setBusy(false); }
      }}>
      <Download className="mr-1 h-3.5 w-3.5" />{busy ? "Lädt…" : "Packstation-Liste (PDF)"}
    </Button>
  );
}

/**
 * A4 — Lieferant im Kopf der Akte setzen und aendern.
 *
 * Bisher stand hier nur Text („Ohne Lieferant"), obwohl das Backend `PATCH supplier_id`
 * laengst konnte: eine Bestellung, die ohne Lieferant angelegt wurde, liess sich in der
 * Oberflaeche nie mehr zuordnen.
 *
 * DIE SPERRE IST FACHLICH, NICHT KOSMETISCH (Design §7, Codex-Befund 35): sobald eine
 * Bestellung `bestellt` ist, gehoert der Lieferant zum Vorgang — sie wieder auf „ohne
 * Lieferant" zu setzen, wuerde einen Zustand erzeugen, den das Backend fuer diesen Status
 * gar nicht zulaesst. Die leere Auswahl wird deshalb nur im Entwurf angeboten; wechseln
 * darf man den Lieferanten weiterhin jederzeit (ein Wechsel ist eine Korrektur, das
 * Entfernen eine Luecke).
 */
function LieferantFeld({
  order,
  patchOrder,
}: {
  order: { status: string; supplier_id: number | null; supplier_name: string | null };
  patchOrder: ReturnType<typeof usePatchPurchaseOrder>;
}) {
  const { data: suppliersData } = useSuppliers();
  const suppliers = suppliersData?.suppliers ?? [];
  const darfLeerSein = order.status === "entwurf";

  return (
    <span className="inline-flex items-center gap-1.5">
      <label htmlFor="akte-lieferant" className="sr-only">Lieferant</label>
      <select
        id="akte-lieferant"
        aria-label="Lieferant"
        value={order.supplier_id ?? ""}
        disabled={patchOrder.isPending}
        onChange={(e) => {
          const wert = e.target.value === "" ? null : Number(e.target.value);
          patchOrder.mutate(
            { supplier_id: wert },
            {
              onSuccess: () => toast.success(wert === null ? "Lieferant entfernt" : "Lieferant gesetzt"),
              // Der Server sagt hier Praezises („Lieferant kann nach dem Abschicken nicht
              // entfernt werden") — das gehoert dem Nutzer, nicht dem Log.
              onError: (e) => toast.error(fehlerText(e, "Lieferant konnte nicht gespeichert werden")),
            },
          );
        }}
        className="rounded border border-line bg-background px-2 py-0.5 text-xs text-ink"
      >
        {darfLeerSein ? (
          <option value="">Ohne Lieferant</option>
        ) : order.supplier_id === null ? (
          // Bestandsfall: eine bestellte Bestellung OHNE Lieferant (so entstanden, bevor es
          // dieses Feld gab). Der Zustand wird angezeigt, damit die Auswahl nicht luegt —
          // aber er ist nicht mehr WAEHLBAR, sobald einmal ein Lieferant gesetzt wurde.
          <option value="">Ohne Lieferant (bitte setzen)</option>
        ) : null}
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      {!darfLeerSein && (
        <span className="text-[11px] text-ink-2" title="Eine bestellte Bestellung braucht einen Lieferanten — wechseln ist möglich, entfernen nicht.">
          Pflicht ab „bestellt"
        </span>
      )}
    </span>
  );
}

function istPositionsIndex(value: number | null | undefined): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function effektivesZiel(row: ReviewDiffRow, draft: { targetTouched: boolean; target: string } | undefined): string {
  return draft?.targetTouched
    ? draft.target.trim()
    : (row.orderItemId === null ? "" : String(row.orderItemId));
}

/** Plan 2026-10-21 (B5): Stueck-/pcs-Zeile, deren Server-Ziel als Paar/Set gefuehrt wird —
 *  `paar_frage` traegt dann die gerechnete Lesart. Altantworten kennen das Feld nicht. */
function istPaarZeile(row: ReviewDiffRow): boolean {
  return !!row.doc && row.doc.set_einheit === false && !row.doc.blocked
    && row.doc.paar_frage !== undefined && row.doc.source_qty !== null;
}

/**
 * Plan 2026-10-21 (C3/B5): Vorbelegung einer frischen Zeile mit `set_faktor` — nur fuer das
 * Server-Ziel, nie fuer eine Handzuordnung.
 * - C3: die Bestellposition wurde schon mit DIESEM Faktor bestaetigt (Rechnung im Assistenten
 *   oder frueherer Beleg), der Faktor ist heute noch derselbe und die Menge nach Faktor (bei
 *   einer Zusammenfassung die Summe der Gruppe) ist genau die bestaetigte.
 * - B5: pcs-Zeile, deren Paar-Lesart genau die Soll-Menge ergibt.
 * Der Nutzer sieht die Vorbelegung und kann sie aendern; der Server prueft sie erneut.
 */
function setVorbelegung(
  row: ReviewDiffRow,
  review: DocumentReviewResponse,
): { faktor: number; grund: "rechnung" | "beleg" | "paar"; eventId: number | null } | null {
  const doc = row.doc;
  if (!doc || row.orderItemId === null || doc.source_qty === null || doc.set_einheit === undefined) return null;
  const faktorDerZeile = (r: ReviewDiffRow): number | null => {
    if (!r.doc || r.orderItemId !== row.orderItemId || r.doc.source_qty === null) return null;
    if (r.doc.set_einheit) return r.doc.requires_unit_confirmation || r.doc.blocked ? r.doc.unit_factor : null;
    // Gesperrte Paar-Lesart: nie vorbelegen (Review 2026-10-21, Befund 3).
    if (!istPaarZeile(r) || r.doc.paar_frage?.sperre) return null;
    return r.doc.paar_frage?.stueck_je_set ?? null;
  };
  const faktor = faktorDerZeile(row);
  if (faktor === null || faktor < 2) return null;
  const ziel = review.targets?.find((t) => t.order_item_id === row.orderItemId);
  const b = ziel?.bestaetigt ?? null;
  if (b && b.stueck_je_set === faktor && ziel?.stueck_je_set === faktor) {
    const gruppe = (review.aggregations ?? []).find((g) => istPositionsIndex(row.positionIndex)
      && g.position_indices.includes(row.positionIndex));
    const zeilen = gruppe
      ? review.rows.filter((r) => istPositionsIndex(r.positionIndex) && gruppe.position_indices.includes(r.positionIndex))
      : [row];
    const faktoren = zeilen.map(faktorDerZeile);
    if (faktoren.every((f) => f === faktor)) {
      const menge = zeilen.reduce((summe, r) => summe + r.doc!.source_qty! * faktor, 0);
      if (menge === b.menge) {
        return { faktor, grund: b.quelle === "beleg" ? "beleg" : "rechnung", eventId: b.event_id };
      }
    }
  }
  if (istPaarZeile(row) && doc.paar_frage && row.soll && doc.paar_frage.qty === row.soll.qty) {
    return { faktor, grund: "paar", eventId: null };
  }
  return null;
}

function reviewZeilenFingerabdruck(row: ReviewDiffRow): string {
  if (!istPositionsIndex(row.positionIndex) || !row.doc) return "";
  return JSON.stringify({
    positionIndex: row.positionIndex,
    code: row.code,
    doc: row.doc,
  });
}

export default function PurchaseOrderDetail() {
  const { id: rawId } = useParams();
  const navigate = useNavigate();
  const id = Number(rawId);
  const validId = Number.isSafeInteger(id) && id > 0 ? id : null;
  const { data, isLoading, isError, refetch: refetchOrder } = usePurchaseOrder(validId);
  const order = data?.order ?? null;

  const patchOrder = usePatchPurchaseOrder(id);
  const setStatus = useSetPurchaseOrderStatus(id);
  const createShipment = useCreatePurchaseShipment(id);
  const patchShipment = usePatchPurchaseShipment();
  const deleteShipment = useDeletePurchaseShipment();
  const setCartonException = useSetShipmentItemCartonException();
  const archiveOrder = useArchivePurchaseOrder(id);
  const restoreOrder = useRestorePurchaseOrder(id);
  const deletePayment = useDeletePurchasePayment();
  const patchCost = usePatchPurchaseCost();

  const { data: documents = [] } = usePurchaseDocuments(validId);
  const uploadDoc = useUploadDocument(validId ?? undefined);
  const assignDoc = useAssignDocument();
  const confirmDoc = useConfirmDocument();
  const rejectDoc = useRejectDocument();
  const deleteDoc = useDeleteDocument();
  const reopenDoc = useReopenDocument();
  const ablegenDoc = useAblegenDocument();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const reviewTransferRef = useRef<HTMLInputElement>(null);
  const reviewRowsRef = useRef<HTMLDivElement>(null);
  const [reviewDocId, setReviewDocId] = useState<number | null>(null);
  const [rejectDocId, setRejectDocId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [deleteDocId, setDeleteDocId] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState<string>("");
  const deleteTargetDoc = documents.find((doc) => doc.id === deleteDocId) ?? null;
  const [uploadShipmentId, setUploadShipmentId] = useState<string>("order");
  // „Nur ablegen (ohne Auswertung)“ gilt fuer EINEN Upload-Lauf und springt danach zurueck:
  // eine spaeter hochgeladene Rechnung oder Packliste soll nicht versehentlich unausgewertet
  // liegen bleiben. `uploadLauf` sperrt den Upload-Bereich, solange Dateien laufen.
  const [uploadNurAblegen, setUploadNurAblegen] = useState(false);
  const [uploadLauf, setUploadLauf] = useState<{ aktuell: number; gesamt: number } | null>(null);
  const [reviewShipmentId, setReviewShipmentId] = useState<string>("");
  const [uebernehmenChecked, setUebernehmenChecked] = useState<boolean>(false);
  const [deleteShipmentId, setDeleteShipmentId] = useState<number | null>(null);
  const [deleteShipmentReason, setDeleteShipmentReason] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveAltbestand, setArchiveAltbestand] = useState(false);
  const [archiveWeOffen, setArchiveWeOffen] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeOrderNumber, setPurgeOrderNumber] = useState("");
  const [purgePin, setPurgePin] = useState("");
  const [purgeReason, setPurgeReason] = useState<PurchasePurgeReasonCode>("duplicate_order");
  const [purgeIrreversible, setPurgeIrreversible] = useState(false);
  const [purgeIdempotencyKey, setPurgeIdempotencyKey] = useState(() => crypto.randomUUID());
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const purgeTitleRef = useRef<HTMLHeadingElement>(null);
  const purgeErrorRef = useRef<HTMLDivElement>(null);
  const [correctionDialog, setCorrectionDialog] = useState<{
    kind: "reopen_document" | "carton_exception" | "clear_carton_exception";
    id: number;
  } | null>(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [reviewMappings, setReviewMappings] = useState<Record<number, {
    target: string;
    targetTouched: boolean;
    rowFingerprint: string;
    serverUpdateVisible: boolean;
    decision: "" | "set_faktor" | "already_pieces" | "manual_qty";
    /** Review 2026-10-21 (Befund 3): mit diesem Faktor und Ziel wurde `set_faktor` bestaetigt. */
    bestaetigterFaktor?: number | null;
    bestaetigtesZiel?: string;
    /** Plan 2026-10-21 (C3/B5): vom Server-Stand vorbelegt — `rechnung`/`beleg` mit der
     *  Ereignis-Id der frueheren Bestaetigung, `paar` wenn Quelle × N der Soll-Menge entspricht. */
    vorbelegt?: { grund: "rechnung" | "beleg" | "paar"; eventId: number | null };
    manualQty: string;
    skip: boolean;
  }>>({});
  /** Ausdrueckliche Zustimmung je Zusammenfassungs-Gruppe, geschluesselt nach Bestellposition. */
  const [aggBestaetigt, setAggBestaetigt] = useState<Record<number, boolean>>({});

  const { data: reviewData, isLoading: isReviewLoading, isError: isReviewError, refetch: refetchReview } = useDocumentReview(reviewDocId);
  const reviewMussUebernehmen = reviewData?.doc_type === "packing_list"
    || reviewData?.doc_type === "invoice_packing_combo";

  useEffect(() => {
    setReviewShipmentId(
      reviewData?.purchase_shipment_id ? String(reviewData.purchase_shipment_id) : "",
    );
  }, [reviewData?.document_id, reviewData?.purchase_shipment_id]);

  const reviewUnterstuetztSichereUebernahme = !!reviewData
    && Number.isSafeInteger(reviewData.review_version)
    && reviewData.review_version > 0
    && Array.isArray(reviewData.targets)
    && Array.isArray(reviewData.rows)
    && reviewData.rows.every((row) => Object.prototype.hasOwnProperty.call(row, "positionIndex")
      && (row.positionIndex === null || istPositionsIndex(row.positionIndex)));

  useEffect(() => {
    setUebernehmenChecked(Boolean(
      reviewMussUebernehmen
      && reviewData?.purchase_shipment_id
      && reviewUnterstuetztSichereUebernahme,
    ));
  }, [reviewMussUebernehmen, reviewData?.document_id, reviewData?.purchase_shipment_id, reviewUnterstuetztSichereUebernahme]);

  useEffect(() => {
    if (!reviewData || !reviewUnterstuetztSichereUebernahme) {
      setReviewMappings({});
      setAggBestaetigt({});
      return;
    }
    setAggBestaetigt({});
    setReviewMappings((current) => {
      const next: typeof reviewMappings = {};
      for (const row of reviewData.rows) {
        if (!istPositionsIndex(row.positionIndex) || !row.doc) continue;
        const rowFingerprint = reviewZeilenFingerabdruck(row);
        const previous = current[row.positionIndex];
        const serverTarget = row.orderItemId === null ? "" : String(row.orderItemId);
        if (previous?.rowFingerprint === rowFingerprint) {
          next[row.positionIndex] = {
            ...previous,
            target: previous.targetTouched ? previous.target : serverTarget,
            serverUpdateVisible: previous.serverUpdateVisible
              || (!previous.targetTouched && previous.target !== serverTarget),
          };
        } else {
          const vorbelegung = setVorbelegung(row, reviewData);
          next[row.positionIndex] = {
            target: serverTarget,
            targetTouched: false,
            rowFingerprint,
            serverUpdateVisible: previous !== undefined,
            decision: vorbelegung ? "set_faktor" : "",
            ...(vorbelegung
              ? {
                  bestaetigterFaktor: vorbelegung.faktor,
                  bestaetigtesZiel: serverTarget,
                  vorbelegt: { grund: vorbelegung.grund, eventId: vorbelegung.eventId },
                }
              : {}),
            manualQty: "",
            skip: false,
          };
        }
      }
      return next;
    });
  }, [reviewData, reviewUnterstuetztSichereUebernahme]);

  // W4.3 WE-Bruecke: E8-Erinnerung sichtbar fuer ALLE mit article_purchase (Seite ist
  // ohnehin dahinter). Der Anlege-Knopf zusaetzlich nur mit warehouse_lead — spiegelt
  // requirePermission('warehouse_lead') (exakter Key-Check, kein admin-Bypass).
  const auth = useAuth();
  const hasArticlePurchase = !!auth.user?.permissions?.article_purchase;
  const hasPushPerm = !!auth.user?.permissions?.article_purchase_push;
  const hasPricePerm = !!auth.user?.permissions?.article_purchase_prices;
  const canBookStock = hasArticlePurchase && (hasPushPerm || !!auth.user?.permissions?.warehouse_lead || !!auth.user?.permissions?.warehouse_admin);
  const canConfirmMischEk = hasPricePerm && (hasPushPerm || (canBookStock && order?.arbeitsplatz?.dr_push_enabled === false));
  const canCreateReceipt = hasArticlePurchase && !!auth.user?.permissions?.warehouse_lead;
  const canPurgeCancelledOrder = hasArticlePurchase && !!auth.user?.permissions?.admin_general;
  const canArchiveAltbestand = hasArticlePurchase && !!auth.user?.permissions?.admin_general;
  const purgePreview = usePurchasePurgePreview(id, purgeDialogOpen && canPurgeCancelledOrder && order?.status === "storniert");
  const purgeOrder = usePurgePurchaseOrder(id);
  const [weDialogShipmentId, setWeDialogShipmentId] = useState<number | null>(null);
  // Arbeitsplatz (Schnitt 1): Hauptknoepfe springen in Reiter der Akte, deshalb gesteuert.
  // ?reiter= (Rollen-Postfach, 21.10.2026): die Akte oeffnet den Reiter und rollt hin. Die
  // Knoepfe des Arbeitsplatzes rollen ebenso — der Reiter liegt weit unter dem Arbeitsplatz.
  const [searchParams] = useSearchParams();
  const reiterAusAdresse = AKTE_REITER.find((r) => r === searchParams.get("reiter")) ?? null;
  const [reiter, setReiter] = useState<AkteReiter>(reiterAusAdresse ?? "positionen");
  const akteRef = useRef<HTMLHeadingElement>(null);
  const [rollWunsch, setRollWunsch] = useState(reiterAusAdresse ? 1 : 0);
  const gerollt = useRef(0);
  useEffect(() => {
    // Ohne Abhaengigkeiten: waehrend „Lädt…“ fehlt das Ziel noch, der Wunsch bleibt stehen.
    if (rollWunsch === gerollt.current || !akteRef.current) return;
    gerollt.current = rollWunsch;
    const ruhig = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    akteRef.current.scrollIntoView?.({ behavior: ruhig ? "auto" : "smooth", block: "start" });
  });
  const zeigeReiter = (r: AkteReiter) => {
    setReiter(r);
    setRollWunsch((n) => n + 1);
  };
  const statusSelectRef = useRef<HTMLSelectElement>(null);
  /** Modul 38: Positionen hinzufuegen — loest die frueher eigene Entwurfsseite ab. */
  const [posDialogOffen, setPosDialogOffen] = useState(false);
  // Der PDF-Knopf sperrt waehrend des Laufs: das PDF entsteht auf dem Server, ein
  // zweiter Klick erzeugte einen zweiten Lauf und einen zweiten Download derselben Datei.
  const [pdfLaeuft, setPdfLaeuft] = useState(false);
  const [supplierPdfBusy, setSupplierPdfBusy] = useState(false);
  const supplierPdfLock = useRef(false);
  /**
   * Derselbe Zustand, aber SOFORT lesbar.
   *
   * `disabled={pdfLaeuft}` wirkt erst, wenn React neu gezeichnet hat. Zwei schnelle Klicks
   * koennen vorher beide durchgehen — dann laufen zwei Server-Anfragen und der Nutzer
   * bekommt dieselbe Datei zweimal. Der Riegel muss deshalb im Handler selbst liegen, nicht
   * nur im DOM. (Befund von Reviewer 2, CRITICAL: der Test wartete auf das Attribut und
   * bewies damit nur, dass Browser gesperrte Knoepfe nicht klicken.)
   */
  const pdfLaeuftRef = useRef(false);
  const confirmDelivery = useConfirmShipmentDelivery(weDialogShipmentId ?? 0);
  const shipmentActionRef = useRef(new Set<number>());
  const [deliveryHinweise, setDeliveryHinweise] = useState<Record<number, string>>({});
  // Abgelesene Container-Nr. (P3). Gehoert zur Dialog-Sitzung: ein neu geoeffneter Dialog
  // beginnt leer, ein Wechsel ueber „Zu C<n> wechseln" nimmt die Eingabe mit.
  const [containerAbgleich, setContainerAbgleich] = useState<{ shipmentId: number | null; wert: string }>({ shipmentId: null, wert: "" });
  const containerEingabe = containerAbgleich.shipmentId === weDialogShipmentId ? containerAbgleich.wert : "";

  const [startmengeShipmentId, setStartmengeShipmentId] = useState<number | null>(null);
  const [finalShipmentId, setFinalShipmentId] = useState<number | null>(null);
  const [mischEkShipmentId, setMischEkShipmentId] = useState<number | null>(null);
  const [bestandBuchenShipmentId, setBestandBuchenShipmentId] = useState<number | null>(null);

  const [terminDraft, setTerminDraft] = useState<Record<number, Partial<Record<"crd" | "etd" | "eta_pod" | "eta_door", string>>>>({});
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);

  const withPrices = useMemo(() => order?.paid_usd !== null, [order]);

  if (!validId) return <div className="py-10 text-center text-sm text-coral">Ungültige Bestell-ID.</div>;
  if (isError) return <div className="py-10 text-center text-sm text-coral">Bestellung nicht erreichbar.</div>;
  if (isLoading || !order) return <div className="py-10 text-center text-sm text-ink-2">Lädt…</div>;

  // Der Satz des SERVERS, wenn er einen mitschickt (Modul 41: 409 „Zuordnung überschreitet …",
  // 409 „Zahlung hat Container-Zuordnungen — zuerst lösen", 400 Betrag) — sonst der eigene
  // Rueckfall. `fehlerText` filtert `undecodable` und die `HTTP nnn`-Platzhalter des Clients.
  // Bis Modul 42 verwarf dieser Helfer die Begruendung und zeigte nur den festen Text.
  const err = (rueckfall: string) => (e: unknown) => toast.error(fehlerText(e, rueckfall));

  // Restmengen je Position (Plan 2026-10-19): Ausweisen nur in einer laufenden Bestellung.
  const restJePosition = new Map((order.restmengen?.positionen ?? []).map((r) => [r.order_item_id, r]));
  const containerKennung = containerKennungen(order);
  const darfRestAusweisen = order.status !== "entwurf" && order.status !== "storniert" && !order.archived_at;

  // Dokument-Upload (Plans/2026-10-14-dokument-ablage.md): mehrere Dateien gehen NACHEINANDER
  // an den Server, nicht parallel — jede Datei nimmt dort Bestell- und Hash-Sperre, und jede
  // Meldung bleibt ihrer Datei zuordenbar. Eine einzelne Datei verhaelt sich wie bisher; erst
  // ab zwei Dateien tragen die Meldungen den Dateinamen und am Ende folgt eine Zusammenfassung.
  // `ziel` (Arbeitsplatz) ersetzt Auswahl und „Nur ablegen“ des Reiters Dokumente.
  const dokumenteHochladen = async (dateien: File[], ziel?: { shipmentId: number | undefined; nurAblegen: boolean }) => {
    const parsedShipmentId = uploadShipmentId === "order" ? null : Number(uploadShipmentId);
    const shipmentId = ziel ? ziel.shipmentId : parsedShipmentId !== null
      && Number.isSafeInteger(parsedShipmentId)
      && parsedShipmentId > 0
        ? parsedShipmentId
        : undefined;
    const nurAblegen = ziel ? ziel.nurAblegen : uploadNurAblegen;
    const mehrere = dateien.length > 1;
    const bilanz: UploadBilanz = { gespeichert: 0, ohneSharepoint: 0, ohneAuswertung: 0, vorhanden: 0, fehlgeschlagen: 0 };
    try {
      for (const [index, file] of dateien.entries()) {
        setUploadLauf({ aktuell: index + 1, gesamt: dateien.length });
        const meldung = (text: string) => (mehrere ? `${file.name}: ${text}` : text);
        try {
          const res = await uploadDoc.mutateAsync({ file, orderId: validId, shipmentId, nurAblegen });
          if (res.outcome === "duplicate") {
            bilanz.vorhanden += 1;
            const vorhandenesDokument = documents.find((doc) => doc.id === res.document_id);
            if (!vorhandenesDokument) {
              toast.info(meldung("Dieses Dokument ist bereits vorhanden. Bitte die Dokumentliste neu laden und den bestehenden Eintrag dort öffnen."));
            } else if (vorhandenesDokument.agent_status === "bestaetigt") {
              toast.info(meldung("Dieses Dokument ist bereits bestätigt. Für Änderungen bitte in der Dokumentzeile ‚Korrigieren‘ wählen."));
            } else if (vorhandenesDokument.agent_status === "abgelegt") {
              // Ein abgelegter Beleg hat keinen Review — ihn zu oeffnen liefe ins Leere.
              toast.info(meldung(nurAblegen
                ? "Dieses Dokument ist bereits abgelegt."
                : "Dieses Dokument ist bereits ohne Auswertung abgelegt. Für eine Auswertung den Eintrag löschen und erneut hochladen."));
            } else if (!nurAblegen && !mehrere) {
              toast.info("Dieses Dokument ist bereits vorhanden. Der bestehende Review wird geöffnet.");
              setReviewDocId(res.document_id);
            } else {
              // „Nur ablegen“ wandelt ein vorhandenes Dokument nicht um (der Server meldet nur
              // das Duplikat), und mitten in einem Mehrfach-Lauf oeffnet sich kein Dialog.
              toast.info(meldung(`Dieses Dokument ist bereits vorhanden (${docStatusLabel(vorhandenesDokument.agent_status)}) und bleibt unverändert.`));
            }
            continue;
          }
          bilanz.gespeichert += 1;
          // KI-Ausfall o. ae.: gespeichert, aber unausgewertet — Warnung, kein Fehler.
          const ohneAuswertung = uploadOhneAuswertungText(res.outcome, res.grund);
          if (ohneAuswertung) {
            bilanz.ohneAuswertung += 1;
            if (res.sharepoint === "failed") bilanz.ohneSharepoint += 1;
            toast.warning(meldung(res.sharepoint === "failed"
              ? `${ohneAuswertung} SharePoint-Ablage fehlgeschlagen, Original NICHT archiviert.`
              : ohneAuswertung));
            continue;
          }
          // M3: Graph-Upload-Fehler sichtbar machen — das Dokument ist gespeichert, aber das
          // Original liegt NICHT im SharePoint.
          if (res.sharepoint === "failed") {
            bilanz.ohneSharepoint += 1;
            const ergebnis = res.outcome === "abgelegt" ? "Dokument abgelegt" : "Dokument verarbeitet";
            toast.warning(meldung(`${ergebnis} — SharePoint-Ablage fehlgeschlagen, Original NICHT archiviert`));
          } else {
            toast.success(meldung(res.outcome === "abgelegt" ? "Dokument abgelegt (ohne Auswertung)" : "Dokument hochgeladen"));
          }
        } catch (e) {
          bilanz.fehlgeschlagen += 1;
          toast.error(meldung(fehlerText(e, "Upload fehlgeschlagen")));
        }
      }
    } finally {
      setUploadLauf(null);
      setUploadNurAblegen(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    if (mehrere) {
      const zusammenfassung = uploadZusammenfassung(bilanz, nurAblegen);
      if (bilanz.fehlgeschlagen > 0 || bilanz.ohneSharepoint > 0 || bilanz.ohneAuswertung > 0) toast.warning(zusammenfassung);
      else if (bilanz.gespeichert > 0) toast.success(zusammenfassung);
      else toast.info(zusammenfassung);
    }
  };

  const correctionShipmentItem = correctionDialog?.kind === "carton_exception"
    || correctionDialog?.kind === "clear_carton_exception"
      ? order.shipments.flatMap((shipment) => shipment.items).find((item) => item.id === correctionDialog.id) ?? null
      : null;
  const correctionOrderItem = correctionShipmentItem?.purchase_order_item_id
    ? order.items.find((item) => item.id === correctionShipmentItem.purchase_order_item_id) ?? null
    : null;
  const correctionTargetLabel = correctionOrderItem?.merchant_sku
    ?? correctionOrderItem?.name_snapshot
    ?? (correctionShipmentItem ? `Containerposition #${correctionShipmentItem.id}` : null);

  const weDialogShipment = weDialogShipmentId === null
    ? null
    : order.shipments.find((s) => s.id === weDialogShipmentId) ?? null;
  const weDialogWorkflow = weDialogShipment ? getShipmentWorkflow(weDialogShipment) : null;
  const weDialogIstReparatur = weDialogShipment?.status === "im_wareneingang"
    && weDialogShipment.warehouse_receipt_id === null;
  const weDialogKannBestaetigen = Boolean(weDialogWorkflow
    && (weDialogIstReparatur ? weDialogWorkflow.can_create_receipt : weDialogWorkflow.can_confirm_delivery));

  function saveTermine(shipment: PurchaseShipment) {
    const draft = terminDraft[shipment.id];
    if (!draft || Object.keys(draft).length === 0) return;
    patchShipment.mutate(
      { id: shipment.id, body: draft },
      {
        onSuccess: () => {
          setTerminDraft((d) => ({ ...d, [shipment.id]: {} }));
          toast.success("Termine gespeichert (Historie im Verlauf)");
        },
        onError: err("Termine konnten nicht gespeichert werden"),
      },
    );
  }

  const aggregationen: ReviewAggregation[] = reviewData?.aggregations ?? [];
  /** positionIndex -> Gruppe, damit die Tabelle und die Pruefung dieselbe Wahrheit nutzen. */
  const aggregationFuerZeile = new Map<number, ReviewAggregation>();
  for (const gruppe of aggregationen) {
    for (const index of gruppe.position_indices) aggregationFuerZeile.set(index, gruppe);
  }

  /** Plan 2026-10-20 (B3): Set-Faktor einer Set-Zeile — vom gewaehlten Ziel-Grundartikel
   *  (`null` = dort nicht festgelegt). Ohne Handzuordnung gilt der Server-Treffer. */
  const setFaktorFuer = (row: ReviewDiffRow): number | null => {
    if (!row.doc || !istPositionsIndex(row.positionIndex)) return null;
    // Review 2026-10-21 (Befund 5): Altantwort (Backend vor B-2) kennt `set_einheit` nicht —
    // dort gilt weiter das alte HAO-set × 2 (gesendet als `set_x2`).
    if (row.doc.set_einheit === undefined) return row.doc.unit_factor === 2 ? 2 : null;
    // Gesperrte Paar-Lesart am Server-Ziel: kein „Ja, Paare" — nur Stueck oder Handmenge.
    if (!row.doc.set_einheit) return paarGesperrt(row) ? null : paarFaktorFuer(row);
    const ziel = effektivesZiel(row, reviewMappings[row.positionIndex]);
    if (ziel === "") return null;
    if (row.orderItemId !== null && ziel === String(row.orderItemId)) return row.doc.unit_factor;
    return reviewData?.targets?.find((target) => String(target.order_item_id) === ziel)?.stueck_je_set ?? null;
  };

  /** Plan 2026-10-21 (B5): Faktor, mit dem eine pcs-Zeile als Paare/Sets gelesen werden kann —
   *  Server-Ziel: aus `paar_frage`; Handziel: Faktor des Ziels (>= 2). Sonst null. */
  const paarFaktorFuer = (row: ReviewDiffRow): number | null => {
    if (!row.doc || !istPositionsIndex(row.positionIndex) || !istPaarZeile(row)) return null;
    const ziel = effektivesZiel(row, reviewMappings[row.positionIndex]);
    if (ziel === "") return null;
    if (row.orderItemId !== null && ziel === String(row.orderItemId)) return row.doc.paar_frage?.stueck_je_set ?? null;
    const n = reviewData?.targets?.find((target) => String(target.order_item_id) === ziel)?.stueck_je_set ?? null;
    return n !== null && n >= 2 ? n : null;
  };
  /** Review 2026-10-21 (Befund 3): Paar-Lesart des Server-Ziels gesperrt (Menge/Wertverlust). */
  const paarGesperrt = (row: ReviewDiffRow): boolean => {
    if (!row.doc?.paar_frage?.sperre || !istPositionsIndex(row.positionIndex) || row.orderItemId === null) return false;
    return effektivesZiel(row, reviewMappings[row.positionIndex]) === String(row.orderItemId);
  };
  /** Zeile braucht eine Set/Stueck- bzw. Paar/Stueck-Entscheidung. */
  const brauchtEinheitEntscheidung = (row: ReviewDiffRow): boolean =>
    !!row.doc && (row.doc.requires_unit_confirmation || row.doc.blocked || paarFaktorFuer(row) !== null);

  /** Review 2026-10-21 (Befund 3): eine `set_faktor`-Bestaetigung gilt nur fuer genau den
   *  Faktor und das Ziel, die beim Bestaetigen zu sehen waren. Aendert ein Refetch oder eine
   *  Handzuordnung eines davon, ist die Zeile wieder offen. */
  const setBestaetigungGilt = (row: ReviewDiffRow, draft: (typeof reviewMappings)[number] | undefined): boolean => {
    if (draft?.decision !== "set_faktor") return false;
    const faktor = setFaktorFuer(row);
    return faktor !== null && draft.bestaetigterFaktor === faktor
      && draft.bestaetigtesZiel === effektivesZiel(row, draft);
  };
  const wirksameEntscheidung = (row: ReviewDiffRow, draft: (typeof reviewMappings)[number] | undefined) =>
    draft?.decision === "set_faktor" && !setBestaetigungGilt(row, draft) ? "" : (draft?.decision ?? "");

  const reviewMappingResult: {
    mappings: ConfirmDocumentMapping[];
    skippedPositionIndices: number[];
    aggregationConfirmations: ConfirmDocumentAggregation[];
    error: string | null;
  } = (() => {
    const leer = { mappings: [], skippedPositionIndices: [], aggregationConfirmations: [], error: null };
    if (!reviewData || !uebernehmenChecked) return leer;
    if (!reviewUnterstuetztSichereUebernahme) {
      return {
        ...leer,
        error: "Sichere Positionsübernahme nicht verfügbar: Bitte zuerst das Backend aktualisieren.",
      };
    }
    const mappings: ConfirmDocumentMapping[] = [];
    const skippedPositionIndices: number[] = [];
    const validTargets = new Set((reviewData.targets ?? []).map((target) => target.order_item_id));
    const targetOwners = new Map<number, string>();

    // Eine erkannte Gruppe ohne Haken schreibt NICHTS — der Server lehnt sie ohnehin ab,
    // aber der Nutzer soll den Grund vor dem Klick lesen, nicht danach.
    const offen = aggregationen.find((gruppe) => !aggBestaetigt[gruppe.purchase_order_item_id]);
    if (offen) {
      return {
        ...leer,
        error: `Zeilen ${offen.position_indices.map((index) => index + 1).join(", ")}: `
          + `Zusammenfassung von ${offen.code} zu einer Bestellposition bestätigen.`,
      };
    }

    for (const row of reviewData.rows) {
      if (!istPositionsIndex(row.positionIndex) || !row.doc) continue;
      const draft = reviewMappings[row.positionIndex];
      if (!draft || draft.rowFingerprint !== reviewZeilenFingerabdruck(row)) {
        return {
          ...leer,
          error: `Zeile ${row.positionIndex + 1}: Review wurde aktualisiert. Bitte Angaben erneut prüfen.`,
        };
      }
      const darfAusgelassenWerden = row.orderItemId === null
        && row.matchKind === "unmatched"
        && !aggregationFuerZeile.has(row.positionIndex);
      if (draft?.skip) {
        if (darfAusgelassenWerden) {
          skippedPositionIndices.push(row.positionIndex);
          continue;
        }
        return {
          ...leer,
          error: `Zeile ${row.positionIndex + 1}: wurde inzwischen zugeordnet. Auslassen aufheben und Zuordnung pruefen.`,
        };
      }
      if (row.doc.qty === null && row.doc.source_qty === null) {
        return {
          ...leer,
          error: darfAusgelassenWerden
            ? `Zeile ${row.positionIndex + 1}: Menge fehlt. Nicht uebernehmen waehlen oder Dokument korrigieren.`
            : `Zeile ${row.positionIndex + 1}: Menge fehlt. Dokument korrigieren.`,
        };
      }
      // Ein Refetch desselben Dokuments kann aus einer vorher ungemappten Zeile
      // einen serverseitig zugeordneten Treffer machen. Solange der Nutzer das
      // Auswahlfeld nicht selbst geaendert hat, folgt der Entwurf diesem Treffer.
      // Eine bewusst geleerte Auswahl bleibt dagegen leer und blockiert.
      const targetEntwurf = draft?.targetTouched
        ? draft.target.trim()
        : (row.orderItemId === null ? "" : String(row.orderItemId));
      const target = Number(targetEntwurf);
      if (!Number.isSafeInteger(target) || target <= 0) {
        return { ...leer, error: `Zeile ${row.positionIndex + 1}: Grundartikel auswählen.` };
      }
      if (!validTargets.has(target)) {
        return { ...leer, error: `Zeile ${row.positionIndex + 1}: Der gewählte Grundartikel gehört nicht mehr zu dieser Bestellung.` };
      }
      // Innerhalb einer bestaetigten Gruppe ist dasselbe Ziel genau der Sinn der Sache —
      // ausserhalb bleibt es der alte Doppelzuordnungs-Fehler.
      const gruppe = aggregationFuerZeile.get(row.positionIndex);
      if (gruppe && target !== gruppe.purchase_order_item_id) {
        return {
          ...leer,
          error: `Zeile ${row.positionIndex + 1}: Das Ziel einer bestätigten Zusammenfassung darf nicht geändert werden.`,
        };
      }
      const owner = gruppe
        ? `aggregation:${gruppe.purchase_order_item_id}`
        : `row:${row.positionIndex}`;
      const existingOwner = targetOwners.get(target);
      if (existingOwner !== undefined && existingOwner !== owner) {
        return { ...leer, error: `Zeile ${row.positionIndex + 1}: Derselbe Grundartikel wurde bereits einer anderen Zeile zugeordnet.` };
      }
      targetOwners.set(target, owner);
      if (draft?.decision === "set_faktor" && !setBestaetigungGilt(row, draft)) {
        return {
          ...leer,
          error: setFaktorFuer(row) === null
            ? `Zeile ${row.positionIndex + 1}: Set-Faktor am Grundartikel fehlt — eintragen oder Menge von Hand.`
            : `Zeile ${row.positionIndex + 1}: Set-Faktor oder Grundartikel geändert — Set-Entscheidung erneut bestätigen.`,
        };
      }
      const needsDecision = brauchtEinheitEntscheidung(row);
      if (needsDecision && !draft?.decision) {
        return { ...leer, error: `Zeile ${row.positionIndex + 1}: Set/Stück-Entscheidung bestätigen.` };
      }
      const mapping: ConfirmDocumentMapping = {
        position_index: row.positionIndex,
        purchase_order_item_id: target,
      };
      if (draft?.decision) mapping.quantity_decision = draft.decision;
      if (draft?.decision === "set_faktor") {
        if (row.doc.set_einheit === undefined) {
          // Altes Backend: kennt nur das feste set × 2.
          mapping.quantity_decision = "set_x2";
        } else {
          mapping.stueck_je_set = draft.bestaetigterFaktor!;
          if (draft.vorbelegt?.eventId != null) mapping.vorbelegt_aus = draft.vorbelegt.eventId;
        }
      }
      if (draft?.decision === "manual_qty") {
        const manualQty = Number(draft.manualQty);
        if (!Number.isSafeInteger(manualQty) || manualQty <= 0) {
          return { ...leer, error: `Zeile ${row.positionIndex + 1}: gültige Stückmenge eintragen.` };
        }
        mapping.manual_qty = manualQty;
      }
      mappings.push(mapping);
    }
    return {
      mappings,
      skippedPositionIndices,
      aggregationConfirmations: aggregationen.map((gruppe) => ({
        purchase_order_item_id: gruppe.purchase_order_item_id,
        position_indices: gruppe.position_indices,
        confirmed: true as const,
      })),
      error: null,
    };
  })();

  const offeneSetZeilen = reviewData?.rows.filter((row) => {
    if (!istPositionsIndex(row.positionIndex) || !row.doc) return false;
    const draft = reviewMappings[row.positionIndex];
    const draftIsCurrent = draft?.rowFingerprint === reviewZeilenFingerabdruck(row);
    return !(draftIsCurrent && draft.skip)
      && !(draftIsCurrent && wirksameEntscheidung(row, draft))
      && setFaktorFuer(row) !== null
      && row.doc.source_qty !== null
      && brauchtEinheitEntscheidung(row);
  }) ?? [];

  const offeneZusatzzeilen = reviewData?.rows.filter((row) => {
    if (!istPositionsIndex(row.positionIndex) || !row.doc) return false;
    const draft = reviewMappings[row.positionIndex];
    const draftIsCurrent = draft?.rowFingerprint === reviewZeilenFingerabdruck(row);
    return row.orderItemId === null
      && row.matchKind === "unmatched"
      && !aggregationFuerZeile.has(row.positionIndex)
      && !(draftIsCurrent && draft.skip)
      && !(draftIsCurrent && draft.targetTouched && draft.target.trim() !== "");
  }) ?? [];

  function fokussiereUebernahmeOderTabelle() {
    requestAnimationFrame(() => {
      if (reviewTransferRef.current && !reviewTransferRef.current.disabled) {
        reviewTransferRef.current.focus();
        return;
      }
      reviewRowsRef.current?.focus();
    });
  }

  // Je Faktor ein eigener Sammelknopf — Zeilen mit verschiedenem Faktor nie gemeinsam.
  const offeneSetFaktoren = [...new Set(offeneSetZeilen.map((row) => setFaktorFuer(row)!))].sort((a, b) => a - b);

  function bestaetigeAlleOffenenSets(faktor: number) {
    const zeilen = offeneSetZeilen.filter((row) => setFaktorFuer(row) === faktor);
    if (!reviewData || zeilen.length === 0) return;
    setReviewMappings((current) => {
      const next = { ...current };
      for (const row of zeilen) {
        if (!istPositionsIndex(row.positionIndex) || !row.doc) continue;
        const previous = current[row.positionIndex];
        const rowFingerprint = reviewZeilenFingerabdruck(row);
        const previousIsCurrent = previous?.rowFingerprint === rowFingerprint;
        next[row.positionIndex] = {
          target: previousIsCurrent
            ? previous.target
            : (row.orderItemId === null ? "" : String(row.orderItemId)),
          targetTouched: previousIsCurrent ? previous.targetTouched : false,
          rowFingerprint,
          serverUpdateVisible: previousIsCurrent
            ? previous.serverUpdateVisible
            : previous !== undefined,
          decision: "set_faktor",
          bestaetigterFaktor: faktor,
          bestaetigtesZiel: effektivesZiel(row, previousIsCurrent ? previous : undefined),
          manualQty: previousIsCurrent ? previous.manualQty : "",
          skip: previousIsCurrent ? previous.skip : false,
        };
      }
      return next;
    });
    fokussiereUebernahmeOderTabelle();
  }

  function markiereZusatzzeileAlsNichtUebernehmen(row: ReviewDiffRow) {
    if (!istPositionsIndex(row.positionIndex) || !row.doc) return;
    setReviewMappings((current) => {
      const previous = current[row.positionIndex!];
      const rowFingerprint = reviewZeilenFingerabdruck(row);
      const previousIsCurrent = previous?.rowFingerprint === rowFingerprint;
      return {
        ...current,
        [row.positionIndex!]: {
          target: previousIsCurrent ? previous.target : "",
          targetTouched: previousIsCurrent ? previous.targetTouched : false,
          rowFingerprint,
          serverUpdateVisible: previousIsCurrent
            ? previous.serverUpdateVisible
            : previous !== undefined,
          decision: previousIsCurrent ? previous.decision : "",
          manualQty: previousIsCurrent ? previous.manualQty : "",
          skip: true,
        },
      };
    });
    fokussiereUebernahmeOderTabelle();
  }

  return (
    <div className="space-y-4">
      <PositionenHinzufuegenDialog
        orderId={id}
        offen={posDialogOffen}
        onClose={() => setPosDialogOffen(false)}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/einkauf" className="text-ink-2 hover:text-ink"><ArrowLeft className="size-4" /></Link>
          <div>
            <div className="text-lg font-semibold text-ink">Bestellung {order.order_number}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-2">
              <LieferantFeld order={order} patchOrder={patchOrder} />
              <span>· angelegt {fmtDate(order.created_at)}</span>
              {order.payment_status ? <span>· Zahlung: {paymentStatusLabel(order.payment_status)}</span> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={order.items.length === 0}
            title={order.items.length === 0 ? "Keine Positionen zum Exportieren" : "Positionen als CSV herunterladen"}
            onClick={() => {
              // A12: entsteht vollstaendig im Browser aus den Daten, die die Akte ohnehin
              // zeigt — kein zweiter Weg zur Wahrheit, kein Backend-Aufruf.
              const csv = bestellungAlsCsv(order.items, hasPricePerm);
              csvHerunterladen(csvDateiname(order.order_number), csv);
              toast.success(`CSV mit ${order.items.length} Positionen erstellt`);
            }}
          >
            <Download className="size-3.5" /> CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={pdfLaeuft}
            title="Interne Bestellübersicht; Preise entsprechend Berechtigung"
            onClick={() => {
              // Internal PDF: server decides whether prices are included.
              if (pdfLaeuftRef.current) return;
              pdfLaeuftRef.current = true;
              setPdfLaeuft(true);
              bestellungPdfHerunterladen(order.id, order.order_number)
                .then(() => toast.success("PDF erstellt"))
                // `downloadPdf` liest den Satz des Servers aus dem ack-Fehlerkoerper und
                // wirft ihn als Error — deshalb hier `e.message` und nicht `fehlerText`
                // (das gilt fuer ApiError aus dem JSON-Weg). Sein eigener Rueckfall
                // („Download fehlgeschlagen (HTTP 502)") ist aber kein Satz des Servers,
                // sondern ein Platzhalter — der bleibt draussen.
                .catch((e: unknown) => {
                  const satz = e instanceof Error ? e.message.trim() : "";
                  toast.error(satz !== "" && !istTransportmeldung(satz)
                    ? satz
                    : "PDF konnte nicht erstellt werden");
                })
                .finally(() => {
                  pdfLaeuftRef.current = false;
                  setPdfLaeuft(false);
                });
            }}
          >
            <Download className="size-3.5" /> {pdfLaeuft ? "Interne PDF …" : "Interne PDF"}
          </Button>
          {hasArticlePurchase && <Button size="sm" variant="outline" className="gap-1.5"
            disabled={supplierPdfBusy} title="Englische Lieferanten-PDF ohne Preise und interne Notizen"
            onClick={() => {
              if (supplierPdfLock.current) return;
              supplierPdfLock.current = true; setSupplierPdfBusy(true);
              lieferantenPdfHerunterladen(order.id, order.order_number)
                .then(() => toast.success("Lieferanten-PDF erstellt"))
                .catch((error: unknown) => {
                  const message = error instanceof Error ? error.message.trim() : '';
                  toast.error(message && !istTransportmeldung(message) ? message : 'Lieferanten-PDF konnte nicht erstellt werden');
                })
                .finally(() => { supplierPdfLock.current = false; setSupplierPdfBusy(false); });
            }}><Download className="size-3.5" />{supplierPdfBusy ? 'Lieferanten-PDF (EN) …' : 'Lieferanten-PDF (EN)'}</Button>}
          <select
            ref={statusSelectRef}
            value={order.status}
            disabled={Boolean(order.archived_at)}
            onChange={(e) =>
              setStatus.mutate(e.target.value, {
                onSuccess: () => toast.success("Status geändert"),
                // Frueher fest „(E17)". Ein G2-409 („Ohne Lieferant/Position kann nicht
                // bestellt werden") ist aber eine ANDERE Regel — die feste Nummer war eine
                // falsche Auskunft. Der Rueckfall nennt deshalb gar keine Regel mehr:
                // lieber unspezifisch als praezise falsch.
                onError: (e) => toast.error(fehlerText(e, "Statuswechsel nicht möglich")),
              })
            }
            className="rounded border border-line bg-background px-2 py-1 text-xs text-ink"
            aria-label="Bestell-Status"
          >
            {ORDER_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{orderStatusLabel(s)}</option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            className={order.archived_at ? "" : "text-red-600 dark:text-red-400"}
            disabled={!hasArticlePurchase}
            onClick={() => {
              setArchiveReason("");
              setArchiveAltbestand(false);
              setArchiveWeOffen(false);
              setArchiveDialogOpen(true);
            }}
          >
            {order.archived_at ? "Bestellung wiederherstellen" : "Bestellung archivieren"}
          </Button>
          {order.status === "storniert" && canPurgeCancelledOrder ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setPurgeOrderNumber("");
                setPurgePin("");
                setPurgeReason("duplicate_order");
                setPurgeIrreversible(false);
                setPurgeIdempotencyKey(crypto.randomUUID());
                setPurgeError(null);
                setPurgeDialogOpen(true);
              }}
            >
              Loeschdialog fuer stornierte Bestellung oeffnen
            </Button>
          ) : null}
        </div>
      </div>

      {order.archived_at ? (
        <div id="archived-readonly" role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
          <span className="font-semibold">Bestellung archiviert.</span> Die Akte ist schreibgeschützt; zum Korrigieren bitte zuerst begründet wiederherstellen.
        </div>
      ) : null}

      {order.arbeitsplatz ? (
        <>
          <Arbeitsplatz
            order={order}
            arbeitsplatz={order.arbeitsplatz}
            documents={documents}
            rechte={{
              canCreateReceipt, hasPushPerm, hasPricePerm, canBookStock, canConfirmMischEk, canEditAkte: hasArticlePurchase,
              darfKostenFreigeben: hasArticlePurchase && !!auth.user?.permissions?.admin_general,
            }}
            uploadLaeuft={uploadLauf !== null}
            aktionen={{
              oeffneWe: setWeDialogShipmentId,
              oeffneBestandBuchen: setBestandBuchenShipmentId,
              oeffneMischEk: setMischEkShipmentId,
              zeigeReiter,
              fokusStatus: () => statusSelectRef.current?.focus(),
              // Arbeitsplatz-Upload: immer MIT Auswertung, Ziel aus der Spur — unabhaengig von
              // Auswahl und „Nur ablegen“ im Reiter Dokumente.
              belegeHochladen: (dateien, shipmentId) =>
                void dokumenteHochladen(dateien, { shipmentId: shipmentId ?? undefined, nurAblegen: false }),
              // Schnitt 2: dieselben Dialoge wie Reiter Dokumente, Container und Positionen.
              pruefeBeleg: (documentId) => { setReviewDocId(documentId); setUebernehmenChecked(false); },
              korrektur: (kind, korrekturId) => { setCorrectionReason(""); setCorrectionDialog({ kind, id: korrekturId }); },
              oeffnePositionen: () => setPosDialogOffen(true),
              // Schnitt 3: dieselben Dialoge wie die Knoepfe im Reiter Dokumente.
              lehneBelegAb: (documentId) => { setRejectDocId(documentId); setRejectReason(""); },
              loescheBeleg: (documentId) => { setDeleteDocId(documentId); setDeleteReason(""); },
            }}
          />
          <h2 ref={akteRef} className="scroll-mt-4 font-display text-sm font-bold uppercase tracking-wide text-ink-2">Akte zum Nachschlagen</h2>
        </>
      ) : null}

      <Tabs value={reiter} onValueChange={(v) => setReiter(v as AkteReiter)}>
        <TabsList>
          <TabsTrigger value="positionen">Positionen ({order.items.length})</TabsTrigger>
          <TabsTrigger value="container">Container ({order.shipments.length})</TabsTrigger>
          <TabsTrigger value="zahlungen">Zahlungen ({order.payments.length})</TabsTrigger>
          <TabsTrigger value="verlauf">Verlauf ({order.events.length})</TabsTrigger>
          <TabsTrigger value="dokumente">Dokumente ({documents.length})</TabsTrigger>
        </TabsList>
        <fieldset disabled={Boolean(order.archived_at)} aria-describedby={order.archived_at ? "archived-readonly" : undefined} className="min-w-0 border-0 p-0">

        <TabsContent value="positionen">
          <Panel title="Positionen" subtitle="Entwurfs-/PI-Ebene — Nachträge möglich (E5)">
            {order.items.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-2">
                Noch keine Positionen — über „Positionen hinzufügen" ergänzen.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Positionen der Bestellung">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">#</th>
                      <th className="py-2 pr-3">Sternnummer</th>
                      <th className="py-2 pr-3">Artikel</th>
                      <th className="py-2 pr-3 text-right">bestellt</th>
                      <th className="py-2 pr-3 text-right">verschifft</th>
                      <th className="py-2 pr-3 text-right">offen</th>
                      {withPrices && <th className="py-2 pr-3 text-right">EK (USD)</th>}
                      <th className="py-2 pr-3">Anforderung</th>
                      <th className="py-2 pr-3">Reklamation</th>
                      <th className="py-2 pr-3">Lösung</th>
                      <th className="py-2 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr key={item.id} className="border-b border-line/50 align-top">
                        <td className="py-2 pr-3 text-ink-2">{item.position}</td>
                        <td className="py-2 pr-3 font-medium"><GrundartikelLink code={item.merchant_sku} /></td>
                        <td className="py-2 pr-3">{item.name_snapshot ?? "—"} <AusverkaufAbzeichen {...item} />
                          {!!item.bausatz_teile?.length && <div className="text-xs text-ink-2 mt-0.5">
                            = {item.bausatz_teile.map((t, i) => <span key={i}>{i > 0 && " + "}{t.menge}× <GrundartikelLink code={t.merchant_sku || t.sku} /></span>)} je Set
                          </div>}
                        </td>
                        <td className="py-2 pr-3 text-right">{mengeMitEinheit(item.qty, item.einheit)}</td>
                        <td className="py-2 pr-3">
                          <VerschifftZelle rest={restJePosition.get(item.id)} einheit={item.einheit} kennung={containerKennung} />
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <OffenZelle
                            rest={restJePosition.get(item.id)} einheit={item.einheit}
                            sku={item.merchant_sku ?? `Pos. ${item.position}`} darfAusweisen={darfRestAusweisen}
                          />
                        </td>
                        {withPrices && <td className="py-2 pr-3 text-right">{formatUsd(item.unit_price_usd)}</td>}
                        <td className="py-2 pr-3"><PositionNotiz item={item} feld="anforderungs_notiz" /></td>
                        <td className="py-2 pr-3"><PositionNotiz item={item} feld="reklamation" /></td>
                        <td className="py-2 pr-3"><PositionNotiz item={item} feld="reklamation_loesung" /></td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1">
                            <AnforderungAnGaKnopf item={item} />
                            <PositionLoeschenKnopf item={item} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <UnzugeordnetHinweis restmengen={order.restmengen} kennung={containerKennung} />
            <div className="mt-3">
              {/* Ersetzt den frueheren Sprung auf die Seite /einkauf/:id/entwurf: Positionen
                  werden dort bearbeitet, wo die Bestellung ohnehin offen ist. */}
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPosDialogOffen(true)}>
                <Plus className="size-3.5" /> Positionen hinzufügen
              </Button>
            </div>
          </Panel>
          <MengenTabelle className="mt-3" tabelle={order.mengen_tabelle} kennung={containerKennung} />
        </TabsContent>

        <TabsContent value="container">
          <div className="space-y-3">
            {order.shipments.map((s) => (
              <Panel
                key={s.id}
                title={s.label ?? `Container #${s.id}`}
                subtitle={`Status: ${shipmentStatusLabel(s.status)}`}
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                  <ContainerStatusAktionen
                    shipment={s}
                    darfKorrigieren={hasArticlePurchase && hasPricePerm}
                    gesperrt={confirmDelivery.isPending}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 dark:text-red-400"
                    aria-describedby={s.warehouse_receipt_id !== null || ["zugestellt", "im_wareneingang", "eingelagert"].includes(s.status)
                      ? `delete-shipment-blocker-${s.id}`
                      : undefined}
                    disabled={!hasArticlePurchase || s.warehouse_receipt_id !== null || ["zugestellt", "im_wareneingang", "eingelagert"].includes(s.status)}
                    onClick={() => { setDeleteShipmentId(s.id); setDeleteShipmentReason(""); }}
                  >
                    <Trash2 className="mr-1 size-3.5" /> Container löschen
                  </Button>
                  {(s.warehouse_receipt_id !== null || ["zugestellt", "im_wareneingang", "eingelagert"].includes(s.status)) ? (
                    <span id={`delete-shipment-blocker-${s.id}`} className="max-w-56 text-xs text-ink-2">Nach Zustellung oder Wareneingang kann der Container nicht gelöscht werden.</span>
                  ) : null}
                  </div>
                }
              >
                <ShipmentWorkflowStepper shipment={s} />
                {getShipmentWorkflow(s).blockers
                  .filter((blocker) => blocker.itemId !== undefined
                    && (blocker.code === "packing_missing" || blocker.code === "packing_inconsistent"))
                  .map((blocker) => {
                    const shipmentItem = s.items.find((item) => item.id === blocker.itemId);
                    const orderItem = shipmentItem?.purchase_order_item_id
                      ? order.items.find((item) => item.id === shipmentItem.purchase_order_item_id)
                      : null;
                    const target = orderItem?.merchant_sku ?? orderItem?.name_snapshot ?? `Position #${blocker.itemId}`;
                    return (
                      <Button
                        key={`${blocker.code}-${blocker.itemId}`}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mb-3 mr-2 text-amber-800 dark:text-amber-300"
                        disabled={!hasArticlePurchase}
                        aria-label={`Packdaten-Ausnahme für ${target} begründen`}
                        onClick={() => {
                          setCorrectionReason("");
                          setCorrectionDialog({ kind: "carton_exception", id: blocker.itemId! });
                        }}
                      >
                        Packdaten-Ausnahme für {target} begründen
                      </Button>
                    );
                  })}
                {/* W4.3 WE-Bruecke: Badge+Link wenn WE existiert; sonst bei ZUGESTELLT
                    amber Erinnerung (E8, alle mit article_purchase) + Anlege-Knopf (nur warehouse_lead). */}
                {s.warehouse_receipt_id ? (
                  <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line bg-panel-2 px-3 py-2">
                    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${weStatusBadgeClass(s.we_status)}`}>
                      WE #{s.warehouse_receipt_id} · {weStatusLabel(s.we_status ?? "offen")}
                    </span>
                    <Link to={`/warehouse/wareneingang/${s.warehouse_receipt_id}`} className="text-xs text-primary hover:underline">
                      Zum Wareneingang →
                    </Link>
                    <PackstationListeKnopf receiptId={s.warehouse_receipt_id} />
                  </div>
                ) : s.status === "im_wareneingang" ? (
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-coral/40 bg-coral/10 px-3 py-2 text-sm text-coral">
                    <span><span className="font-medium">Korrektur erforderlich:</span> Status ist „Im Wareneingang“, aber die WE-Verknüpfung fehlt.</span>
                    {canCreateReceipt ? (
                      <Button
                        size="sm"
                        disabled={!getShipmentWorkflow(s).can_create_receipt}
                        aria-describedby={!getShipmentWorkflow(s).can_create_receipt ? `receipt-repair-blocker-${s.id}` : undefined}
                        onClick={() => setWeDialogShipmentId(s.id)}
                      >
                        Fehlenden Wareneingang sicher anlegen
                      </Button>
                    ) : <span className="text-xs">Die Lagerleitung kann den fehlenden Wareneingang protokolliert anlegen.</span>}
                    {!getShipmentWorkflow(s).can_create_receipt ? (
                      <span id={`receipt-repair-blocker-${s.id}`} className="text-xs">
                        {s.workflow
                          ? "Packliste und Packdaten müssen zuerst vollständig bestätigt sein."
                          : "Serverseitige Workflowdaten fehlen. Bitte zuerst das Backend aktualisieren und die Akte neu laden."}
                      </span>
                    ) : null}
                  </div>
                ) : hasArticlePurchase && s.status !== "eingelagert" ? (
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
                    <div>
                      <div className="font-medium">
                        {s.status === "zugestellt" ? "Zustellung bestätigt — Wareneingang noch offen" : "Ware physisch angekommen?"}
                      </div>
                      <div className="text-xs opacity-90">Die Bestätigung setzt den Container auf „Zugestellt“ und legt den vorbefüllten Wareneingang an.</div>
                      {!getShipmentWorkflow(s).can_confirm_delivery ? (
                        <div id={`delivery-blocker-${s.id}`} className="mt-1 text-xs font-semibold">
                          Nächster Schritt: {getShipmentWorkflow(s).blockers[0]?.message
                            ?? (!s.workflow
                              ? "Serverseitige Workflowdaten fehlen. Bitte zuerst das Backend aktualisieren und die Akte neu laden."
                              : getShipmentWorkflow(s).current_step === "packlist"
                              ? "Packliste hochladen und bestätigen."
                              : "Die angezeigten Voraussetzungen abschließen und die Akte neu laden.")}
                        </div>
                      ) : null}
                    </div>
                    {canCreateReceipt ? (
                      <Button
                        size="sm"
                        disabled={confirmDelivery.isPending || !getShipmentWorkflow(s).can_confirm_delivery}
                        aria-describedby={!getShipmentWorkflow(s).can_confirm_delivery ? `delivery-blocker-${s.id}` : undefined}
                        onClick={() => {
                          if (shipmentActionRef.current.has(s.id)) return;
                          setWeDialogShipmentId(s.id);
                        }}
                      >
                        Zustellung bestätigen und Wareneingang anlegen
                      </Button>
                    ) : (
                      <span className="max-w-sm text-xs">Die Lagerleitung bestätigt die Zustellung und legt den Wareneingang an.</span>
                    )}
                  </div>
                ) : null}
                {deliveryHinweise[s.id] && !s.warehouse_receipt_id ? (
                  <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
                    {deliveryHinweise[s.id]}
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                  {([
                    ["container_no", "Container-Nr."], ["bl_no", "B/L-Nr."], ["booking_ref", "Booking"],
                    ["spediteur_ref", "Spediteur-Ref."], ["lieferanten_ref", "Lieferanten-Ref."],
                  ] as const).map(([field, label]) => (
                    <label key={field} className="text-xs text-ink-2">
                      {label}
                      <Input
                        defaultValue={s[field] ?? ""}
                        className="mt-1 h-8 text-xs"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (s[field] ?? "")) {
                            patchShipment.mutate(
                              { id: s.id, body: { [field]: v || null } },
                              { onError: err("Speichern fehlgeschlagen") },
                            );
                          }
                        }}
                      />
                    </label>
                  ))}
                </div>
                {/* Termin-Leiste CRD → ETD → ETA POD → ETA Door (Historie via Verlauf) */}
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
                  {([["crd", "CRD"], ["etd", "ETD"], ["eta_pod", "ETA Hafen"], ["eta_door", "ETA Tür"]] as const).map(([field, label]) => (
                    <label key={field} className="text-xs text-ink-2">
                      {label}
                      <Input
                        type="date"
                        defaultValue={s[field] ?? ""}
                        className="mt-1 h-8 text-xs"
                        onChange={(e) =>
                          setTerminDraft((d) => ({ ...d, [s.id]: { ...d[s.id], [field]: e.target.value } }))
                        }
                      />
                    </label>
                  ))}
                  <div className="flex items-end">
                    <Button size="sm" variant="outline" onClick={() => saveTermine(s)} disabled={patchShipment.isPending}>
                      Termine speichern
                    </Button>
                  </div>
                </div>
                {s.items.length > 0 && <div className="mt-4 space-y-2">
                  <div className="text-xs font-medium text-ink">Packstation-Direktanteil je Position</div>
                  {s.items.map(si => <PackstationAnteil key={si.id} item={si}
                    stern={order.items.find(it => it.id === si.purchase_order_item_id)?.merchant_sku ?? `Position #${si.id}`}
                    locked={!hasArticlePurchase || Boolean(order.archived_at) || s.warehouse_receipt_id !== null
                      || ['zugestellt', 'im_wareneingang', 'eingelagert'].includes(s.status)} />)}
                </div>}
                {/* Kosten-Checkliste (E3/E18); Soll Spedition + Zoll seit P1 (Plan 2026-10-18) */}
                <div className="mt-4" role="region" aria-label={`Kosten für Misch-EK ${s.label ?? `Container #${s.id}`}`}>
                  <div className="mb-2 text-xs font-medium text-ink">Nebenkosten</div>
                  {s.costs.length === 0 ? null : (
                    <ul className="space-y-1">
                      {s.costs.map((c) => (
                        <li key={c.id} className="flex items-center gap-3 text-xs">
                          <span className="w-20 font-medium">{c.typ}</span>
                          <span className="w-40 text-ink-2">{c.label ?? "—"}</span>
                          <span className="w-24 text-right">{c.amount_eur !== null ? formatEuro(c.amount_eur) : "—"}</span>
                          {hasPricePerm ? (
                            <select
                              value={c.status}
                              onChange={(e) =>
                                patchCost.mutate(
                                  { id: c.id, body: kostenPatchBody(c, e.target.value, c.amount_eur) },
                                  { onError: err("Kosten-Status konnte nicht gespeichert werden") },
                                )
                              }
                              className="rounded border border-line bg-background px-1.5 py-0.5 text-xs"
                              aria-label={`Kosten-Status ${c.typ} ${c.id}`}
                            >
                              {["erwartet", "erfasst", "entfaellt", "geschaetzt"].map((st) => (
                                <option key={st} value={st}>{st}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-ink-2">{c.status}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <KostenSollHinweis anzahlPosten={s.costs.length} />
                  <KostenSollAktionen
                    shipmentId={s.id}
                    costs={s.costs}
                    darfAnlegen={hasPricePerm}
                    darfFreigeben={hasArticlePurchase && !!auth.user?.permissions?.admin_general}
                  />
                  {hasPricePerm ? <NeuerKostenposten shipmentId={s.id} err={err} /> : null}
                </div>
                {/* Interne Bestandsbuchung und externe DR-Aktionen haben getrennte Rechte. */}
                {canBookStock && (
                  <div className="mt-4 pt-3 border-t border-line/60">
                    <div className="mb-2 text-xs font-semibold text-ink">Bestand-Aktionen</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {canBookStock && s.status === "im_wareneingang" && (
                        <Button size="sm" variant="outline" onClick={() => setBestandBuchenShipmentId(s.id)}>
                          Bestand buchen…
                        </Button>
                      )}
                      {hasPushPerm && ["zugestellt", "im_wareneingang"].includes(s.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStartmengeShipmentId(s.id)}
                        >
                          Null-Artikel-Schnellstart
                        </Button>
                      )}
                      {hasPushPerm && ["im_wareneingang", "eingelagert"].includes(s.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setFinalShipmentId(s.id)}
                        >
                          Einlagerung prüfen & final pushen
                        </Button>
                      )}
                      {canConfirmMischEk && ["im_wareneingang", "eingelagert"].includes(s.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setMischEkShipmentId(s.id)}
                        >
                          Misch-EK…
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </Panel>
            ))}
            <Button
              variant="outline" size="sm" className="gap-1.5"
              disabled={createShipment.isPending}
              onClick={() =>
                createShipment.mutate(
                  { label: `Container ${order.shipments.length + 1}` },
                  { onSuccess: () => toast.success("Container angelegt"), onError: err("Container fehlgeschlagen") },
                )
              }
            >
              <Plus className="size-3.5" /> Container anlegen
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="zahlungen">
          <Panel
            title="Zahlungen"
            subtitle={withPrices
              ? `Rechnungssumme ${formatUsd(order.invoice_total_usd)} · bezahlt ${formatUsd(order.paid_usd)}`
              : "Beträge nur mit Preis-Berechtigung sichtbar (E13)"}
          >
            {withPrices && hasPricePerm && (
              <RechnungssummeFeld orderId={order.id} invoice={order.invoice_total_usd} err={err} />
            )}
            {order.payments.length === 0 ? (
              <div className="py-4 text-center text-sm text-ink-2">Noch keine Zahlungen erfasst.</div>
            ) : (
              <ul className="space-y-2">
                {order.payments.map((p) => (
                  <li key={p.id} className="rounded border border-line bg-panel-2 p-2.5">
                    <ZahlungsKarte
                      zahlung={p}
                      canEdit={hasPricePerm}
                      err={err}
                      onLoeschen={() =>
                        deletePayment.mutate(p.id, {
                          onSuccess: () => toast.success("Zahlung gelöscht"),
                          onError: err("Löschen fehlgeschlagen"),
                        })
                      }
                    />
                    {/* Anrechnungs-Matrix (E4: manuell) */}
                    <ZahlungAnrechnung
                      zahlung={p}
                      shipments={order.shipments}
                      titelVon={(shipmentId) => order.shipments.find((sh) => sh.id === shipmentId)?.label ?? `Container #${shipmentId}`}
                      darf={hasPricePerm}
                      err={err}
                    />
                  </li>
                ))}
              </ul>
            )}
            {/* Neue Zahlung */}
            {hasPricePerm ? (
              <NeueZahlung orderId={order.id} err={err} />
            ) : (
              <div className="mt-4 rounded border border-line bg-panel-2 p-3 text-xs text-ink-2">
                Zahlungen können nur mit der Berechtigung für Einkaufspreise bearbeitet werden.
              </div>
            )}
          </Panel>
        </TabsContent>

        </fieldset>

        <TabsContent value="verlauf">
          <Panel title="Verlauf" subtitle="Audit-Historie (Statuswechsel, Termin-Änderungen, Erfassungen)">
            {order.events.length === 0 ? (
              <div className="py-4 text-center text-sm text-ink-2">Noch keine Einträge.</div>
            ) : (
              <ul className="space-y-1.5">
                {order.events.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                    <span className="text-ink-2">{new Date(e.created_at).toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}</span>
                    <span className="font-medium">{e.event_type}</span>
                    <span className="text-ink-2">{e.created_by ?? e.source}</span>
                    {e.payload ? <code className="text-[11px] text-ink-2">{JSON.stringify(e.payload)}</code> : null}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="dokumente">
          <Panel
            title="Dokumente"
            subtitle="Hochgeladene und extrahierte Dokumente"
            actions={
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-52 text-xs text-ink-2">
                  Upload-Ziel
                  <select
                    aria-label="Container für Dokument-Upload"
                    value={uploadShipmentId}
                    disabled={uploadLauf !== null}
                    onChange={(e) => setUploadShipmentId(e.target.value)}
                    className="mt-1 block h-8 w-full rounded border border-line bg-background px-2 text-sm text-ink"
                  >
                    <option value="" disabled>Ziel wählen…</option>
                    <option value="order">Nur Bestellung (z. B. Rechnung)</option>
                    {order.shipments.map((shipment, index) => (
                      <option key={shipment.id} value={String(shipment.id)}>
                        {shipment.label || shipment.container_no || `Container ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
                <label
                  className="flex h-8 items-center gap-1.5 text-xs text-ink"
                  title="Die Datei wird gespeichert und zugeordnet, aber nicht ausgelesen: kein Review, keine Termin- oder Positionsänderung."
                >
                  <input
                    type="checkbox"
                    checked={uploadNurAblegen}
                    disabled={uploadLauf !== null}
                    onChange={(e) => setUploadNurAblegen(e.target.checked)}
                    className="rounded border-line"
                  />
                  Nur ablegen (ohne Auswertung)
                </label>
                {uploadLauf && (
                  <span className="text-xs text-ink-2">
                    {uploadLauf.gesamt > 1 ? `lädt ${uploadLauf.aktuell}/${uploadLauf.gesamt}…` : "lädt…"}
                  </span>
                )}
                <input
                  type="file"
                  multiple
                  aria-label="PDF- oder Excel-Dokument auswählen"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const dateien = Array.from(e.target.files ?? []);
                    if (dateien.length === 0) return;
                    void dokumenteHochladen(dateien);
                  }}
                />
                <Button
                  size="sm"
                  disabled={uploadLauf !== null || uploadShipmentId === ""}
                  className="gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus className="size-3.5" /> Upload
                </Button>
              </div>
            }
          >
            {documents.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-2">
                Noch keine Dokumente zugeordnet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">Datei</th>
                      <th className="py-2 pr-3">Typ</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Datum</th>
                      <th className="py-2 pr-3 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id} className="border-b border-line/50 hover:bg-panel-2">
                        <td className="py-2 pr-3 font-medium text-ink">
                          {doc.file_name || doc.sharepoint_path || `Dokument #${doc.id}`}
                        </td>
                        <td className="py-2 pr-3">
                          {doc.typ === "nicht_parsebar" ? (
                            <span className="inline-flex items-center rounded border border-red-500 bg-transparent px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                              {docTypLabel(doc.typ)}
                            </span>
                          ) : (
                            <span>{doc.typ ? docTypLabel(doc.typ) : "—"}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                              doc.agent_status === "extrahiert"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                : doc.agent_status === "bestaetigt"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                                : doc.agent_status === "abgelehnt"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                                : doc.agent_status === "abgelegt"
                                ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"
                                : "bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200"
                            }`}
                          >
                            {docStatusLabel(doc.agent_status)}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-ink-2">{fmtDate(doc.created_at)}</td>
                        <td className="py-2 pr-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {(doc.agent_status === "extrahiert" || doc.agent_status === "bestaetigt") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setReviewDocId(doc.id);
                                  setUebernehmenChecked(false);
                                }}
                              >
                                Review
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              disabled={downloadingDocId === doc.id}
                              onClick={async () => {
                                setDownloadingDocId(doc.id);
                                try {
                                  await dokumentHerunterladen(doc.id, doc.file_name || `document-${doc.id}.pdf`);
                                } catch (err) {
                                  toast.error(err instanceof Error ? err.message : "Download fehlgeschlagen");
                                } finally {
                                  setDownloadingDocId(null);
                                }
                              }}
                            >
                              {downloadingDocId === doc.id ? (
                                <Loader2 className="mr-1 size-3.5 animate-spin" />
                              ) : null}
                              Download
                            </Button>
                            {doc.agent_status === "extrahiert" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                                onClick={() => {
                                  setRejectDocId(doc.id);
                                  setRejectReason("");
                                }}
                              >
                                Ablehnen
                              </Button>
                            )}
                            {(doc.agent_status === "neu" || doc.agent_status === "extrahiert") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                title="Ohne (weitere) Auswertung in der Akte ablegen"
                                disabled={ablegenDoc.isPending}
                                onClick={() => {
                                  const name = doc.file_name || `Dokument ${doc.id}`;
                                  if (!window.confirm(`„${name}“ ohne Auswertung ablegen? Positionen und Termine bleiben unverändert.`)) return;
                                  ablegenDoc.mutate(
                                    { docId: doc.id },
                                    {
                                      onSuccess: () => toast.success("Dokument abgelegt (ohne Auswertung)"),
                                      onError: err("Ablegen fehlgeschlagen"),
                                    },
                                  );
                                }}
                              >
                                Ablegen
                              </Button>
                            )}
                            {doc.agent_status === "bestaetigt"
                              && (doc.typ === "packing_list" || doc.typ === "invoice_packing_combo") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                disabled={!hasArticlePurchase || reopenDoc.isPending}
                                onClick={() => {
                                  setCorrectionReason("");
                                  setCorrectionDialog({ kind: "reopen_document", id: doc.id });
                                }}
                              >
                                Korrigieren
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                              onClick={() => {
                                setDeleteDocId(doc.id);
                                setDeleteReason("");
                              }}
                            >
                              Löschen
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={reviewDocId !== null} onOpenChange={(open) => {
        if (!open) {
          setReviewDocId(null);
          setUebernehmenChecked(false);
          setReviewMappings({});
        }
      }}>
        <DialogContent className="flex max-h-[94dvh] w-[96vw] max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-0 sm:max-w-[1180px]">
          <DialogHeader className="shrink-0 border-b border-line px-6 pb-4 pt-6">
            <DialogTitle>Review: {reviewData ? docTypLabel(reviewData.doc_type) : "Dokument"}</DialogTitle>
            <DialogDescription>
              Rechnung / Bestellung → Packliste / Container → Wareneingang
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {isReviewLoading ? (
              <div className="py-8 text-center text-sm text-ink-2">Lädt…</div>
            ) : isReviewError || !reviewData ? (
              <div className="py-8 text-center text-sm">
              <p className="text-red-600 dark:text-red-400">Review konnte nicht geladen werden.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => void refetchReview()}>
                Erneut versuchen
              </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-2 rounded border border-line bg-panel-2 p-3 text-xs sm:grid-cols-3">
                  <div><span className="font-medium">1. Bestellung</span><br />Sollmenge und Grundartikel</div>
                  <div><span className="font-medium">2. Packliste</span><br />Container, Kartons und Mengen</div>
                  <div><span className="font-medium">3. Wareneingang</span><br />entsteht erst bei bestätigter Zustellung</div>
                </div>

                {(reviewData.doc_type === "packing_list" || reviewData.doc_type === "invoice_packing_combo")
                  && !reviewData.purchase_shipment_id ? (
                    <div
                      role="alert"
                      className="space-y-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
                    >
                      <div>
                        <strong>Noch keinem Container zugeordnet.</strong>{" "}
                        Wähle den Container, damit Kartons, Mengen und Menge je Karton übernommen werden können.
                      </div>
                      {order.shipments.length > 0 ? (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <label className="min-w-64 flex-1 text-xs font-medium">
                            Container für diese Packliste
                            <select
                              aria-label="Container für diese Packliste"
                              value={reviewShipmentId}
                              onChange={(e) => setReviewShipmentId(e.target.value)}
                              className="mt-1 block h-9 w-full rounded border border-amber-400 bg-background px-2 text-sm text-ink"
                            >
                              <option value="">— Container auswählen —</option>
                              {order.shipments.map((shipment, index) => (
                                <option key={shipment.id} value={String(shipment.id)}>
                                  {shipment.label || shipment.container_no || `Container ${index + 1}`}
                                </option>
                              ))}
                            </select>
                          </label>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!reviewShipmentId || assignDoc.isPending}
                            onClick={() => {
                              const shipmentId = Number(reviewShipmentId);
                              const orderId = reviewData.purchase_order_id ?? validId;
                              if (!orderId || !Number.isSafeInteger(shipmentId) || shipmentId <= 0) return;
                              assignDoc.mutate(
                                { docId: reviewData.document_id, orderId, shipmentId },
                                {
                                  onSuccess: async () => {
                                    setUebernehmenChecked(false);
                                    toast.success("Packliste wurde dem Container zugeordnet");
                                    await refetchReview();
                                  },
                                  onError: (error) => toast.error(
                                    fehlerText(error, "Container konnte nicht zugeordnet werden"),
                                  ),
                                },
                              );
                            }}
                          >
                            {assignDoc.isPending ? "Wird zugeordnet…" : "Container zuordnen"}
                          </Button>
                        </div>
                      ) : (
                        <div>Zuerst muss in der Bestellung ein Container angelegt werden.</div>
                      )}
                    </div>
                  ) : null}

                {aggregationen.map((gruppe) => {
                  const zeilen = reviewData.rows.filter(
                    (row) => istPositionsIndex(row.positionIndex) && gruppe.position_indices.includes(row.positionIndex),
                  );
                  const angezeigteMenge = (row: ReviewDiffRow): number => {
                    if (!row.doc || !istPositionsIndex(row.positionIndex)) return 0;
                    const draft = reviewMappings[row.positionIndex];
                    const faktor = setFaktorFuer(row);
                    if (setBestaetigungGilt(row, draft) && row.doc.source_qty !== null && faktor !== null) {
                      return row.doc.source_qty * faktor;
                    }
                    if (draft?.decision === "already_pieces" && row.doc.source_qty !== null) {
                      return row.doc.source_qty;
                    }
                    if (draft?.decision === "manual_qty") {
                      const manualQty = Number(draft.manualQty);
                      if (Number.isSafeInteger(manualQty) && manualQty > 0) return manualQty;
                    }
                    return row.doc.qty ?? 0;
                  };
                  const summeMenge = zeilen.reduce((summe, row) => summe + angezeigteMenge(row), 0);
                  const kartonsVollstaendig = zeilen.every((row) => row.doc?.cartons !== null && row.doc?.cartons !== undefined);
                  const summeKartons = kartonsVollstaendig
                    ? zeilen.reduce((summe, row) => summe + (row.doc?.cartons ?? 0), 0)
                    : null;
                  const ppcWerte = Array.from(new Set(zeilen.map((row) => row.doc?.pieces_per_carton ?? null)));
                  const ppcGemischt = ppcWerte.length > 1;
                  const zeilenNummern = gruppe.position_indices.map((index) => index + 1).join(", ");
                  return (
                    <div
                      key={gruppe.purchase_order_item_id}
                      role="alert"
                      className="space-y-2 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
                    >
                      <div>
                        <strong>Dieselbe Sternnummer mehrfach:</strong> Die Zeilen {zeilenNummern} tragen alle{" "}
                        {gruppe.code} und zeigen auf dieselbe Bestellposition. Ohne ausdrückliche Bestätigung
                        wird nichts übernommen.
                      </div>
                      <div>
                        Zusammengefasst: {summeMenge} Einh. ·{" "}
                        {summeKartons === null
                          ? "Kartons unvollständig – wird nicht gespeichert"
                          : `${summeKartons} Kartons`}{" "}
                        ·{" "}
                        {ppcGemischt
                          ? "Menge je Karton gemischt – wird nicht gespeichert"
                          : `${ppcWerte[0] ?? "—"} Einh. je Karton`}
                      </div>
                      {ppcGemischt ? (
                        <div className="font-medium">
                          Achtung: die Zeilen nennen unterschiedliche Mengen je Karton (
                          {ppcWerte.map((wert) => wert ?? "ohne Angabe").join(", ")}). Der Container speichert
                          dafür keinen Wert.
                        </div>
                      ) : null}
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          aria-label={`Zusammenfassung ${gruppe.code} bestätigen`}
                          checked={!!aggBestaetigt[gruppe.purchase_order_item_id]}
                          onChange={(event) => setAggBestaetigt((current) => ({
                            ...current,
                            [gruppe.purchase_order_item_id]: event.target.checked,
                          }))}
                          className="rounded border-red-300"
                        />
                        <span>Zusammenfassung zu einer Bestellposition bestätigen</span>
                      </label>
                    </div>
                  );
                })}

                {(offeneSetZeilen.length > 0 || offeneZusatzzeilen.length > 0) ? (
                  <div role="region" aria-label="Offene Aufgaben vor der Containerübernahme" className="space-y-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                    <div className="font-medium">Vor der Containerübernahme noch erledigen</div>
                    {offeneSetFaktoren.map((faktor) => {
                      const anzahl = offeneSetZeilen.filter((row) => setFaktorFuer(row) === faktor).length;
                      return (
                        <div key={`set-${faktor}`} className="flex flex-wrap items-center justify-between gap-2">
                          <span>
                            {anzahl} als SET erkannte {anzahl === 1 ? "Zeile benötigt" : "Zeilen benötigen"} die Bestätigung „Set = {faktor} Stück“ (Zählweise am Grundartikel).
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-auto w-full whitespace-normal sm:w-auto"
                            onClick={() => bestaetigeAlleOffenenSets(faktor)}
                          >
                            Alle {anzahl} Set-Zeilen mit Set = {faktor} Stück bestätigen
                          </Button>
                        </div>
                      );
                    })}
                    {offeneZusatzzeilen.map((row) => (
                      <div key={`zusatz-${row.positionIndex}`} className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                          Zeile {row.positionIndex! + 1} hat keine Bestellposition: Grundartikel zuordnen oder ausdrücklich auslassen.
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-auto w-full whitespace-normal sm:w-auto"
                          onClick={() => markiereZusatzzeileAlsNichtUebernehmen(row)}
                        >
                          Zeile {row.positionIndex! + 1} nicht übernehmen
                        </Button>
                      </div>
                    ))}
                    <div className="text-xs">
                      Anschließend unten „Positionen und Kartondaten in Container übernehmen“ aktivieren und bestätigen.
                    </div>
                  </div>
                ) : null}

                <div
                  ref={reviewRowsRef}
                  className="max-w-full overflow-x-auto overscroll-x-contain rounded border border-line"
                  role="region"
                  aria-label="Packlistenpositionen – auf kleinen Bildschirmen seitlich scrollen"
                  tabIndex={0}
                >
                  <table className="min-w-[980px] w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">Zeile</th>
                      <th className="py-2 pr-3">Sternnummer</th>
                      <th className="py-2 pr-3">Packliste Menge / Kartons / EK</th>
                      <th className="py-2 pr-3">Bestellung Menge / EK</th>
                      <th className="py-2 pr-3">Grundartikel in dieser Bestellung</th>
                      <th className="py-2 pr-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewData.rows.map((r, idx) => {
                      let rowBg = "";
                      // Ohne JEDE Menge im Dokument gibt es nichts zu deuten: eine
                      // Set/Stueck-Pflichtauswahl waere hier eine Scheinentscheidung. Die
                      // Zeile muss korrigiert oder, falls ungemappt, ausdruecklich ausgelassen werden.
                      const ohneMenge = !!r.doc && r.doc.qty === null && r.doc.source_qty === null;
                      const needsUnitDecision = !!r.doc && !ohneMenge && brauchtEinheitEntscheidung(r);
                      const paarZeile = paarFaktorFuer(r) !== null;
                      const inAggregation = istPositionsIndex(r.positionIndex)
                        && aggregationFuerZeile.has(r.positionIndex);
                      const hasCartonMismatch = r.doc?.carton_quantity_ok === false
                        && r.doc.qty !== null
                        && r.doc.cartons !== null
                        && r.doc.pieces_per_carton !== null;
                      if (needsUnitDecision || ohneMenge || inAggregation) {
                        rowBg = "bg-red-50 dark:bg-red-950/30";
                      } else if (hasCartonMismatch) {
                        rowBg = "bg-red-50 dark:bg-red-950/30";
                      } else if (r.kategorie === "qty_diff" || r.kategorie === "price_diff") {
                        rowBg = "bg-amber-50 dark:bg-amber-950/30";
                      } else if (r.kategorie === "nur_dokument") {
                        rowBg = "bg-sky-50 dark:bg-sky-950/30";
                      } else if (r.kategorie === "nur_bestellung") {
                        rowBg = "bg-red-50 dark:bg-red-950/30";
                      }

                      let katLabel = "Übereinstimmung";
                      if (r.kategorie === "qty_diff") katLabel = "Mengenabweichung";
                      if (r.kategorie === "price_diff") katLabel = "Preisabweichung";
                      if (r.kategorie === "nur_dokument") katLabel = "Nur im Dokument";
                      if (r.kategorie === "nur_bestellung") katLabel = "Nur in Bestellung";
                      if (r.matchKind === "unique_fallback") katLabel = "Eindeutig ohne Stern zugeordnet";
                      if (r.matchKind === "qty_price_fallback") katLabel = "Eindeutig über Menge/EK zugeordnet";
                      if (r.matchKind === "ambiguous") katLabel = "Mehrdeutig – bitte wählen";

                      const docQtyStr = r.doc?.qty !== null && r.doc?.qty !== undefined
                        ? (r.doc.cartons && r.doc.pieces_per_carton
                            ? `${r.doc.qty} Einh. · ${r.doc.cartons} Kt. × ${r.doc.pieces_per_carton}`
                            : `${r.doc.qty}`)
                        : "—";
                      const aktuellerFingerabdruck = reviewZeilenFingerabdruck(r);
                      const gespeicherterDraft = istPositionsIndex(r.positionIndex) ? reviewMappings[r.positionIndex] : null;
                      const draft = gespeicherterDraft?.rowFingerprint === aktuellerFingerabdruck
                        ? gespeicherterDraft
                        : null;
                      const darfZeileAusgelassenWerden = istPositionsIndex(r.positionIndex)
                        && r.orderItemId === null
                        && r.matchKind === "unmatched"
                        && !aggregationFuerZeile.has(r.positionIndex);
                      const angezeigtesZiel = draft?.targetTouched
                        ? draft.target
                        : (r.orderItemId === null ? "" : String(r.orderItemId));
                      const zielWurdeAktualisiert = draft?.serverUpdateVisible ?? false;
                      const skipBeschriftung = darfZeileAusgelassenWerden
                        ? "Nicht übernehmen"
                        : "Nicht übernehmen (Zuordnung geändert - bitte aufheben und prüfen)";

                      return (
                        <Fragment key={idx}>
                          <tr className={`border-b border-line/50 ${rowBg}`}>
                            <td className="py-2 pr-3 tabular-nums">{istPositionsIndex(r.positionIndex) ? r.positionIndex + 1 : "—"}</td>
                            <td className="py-2 pr-3 font-medium">{r.code ?? (r.orderItemId ? `#${r.orderItemId}` : "—")}</td>
                            <td className="py-2 pr-3">
                              {docQtyStr} · {formatUsd(r.doc?.unit_price_usd)}
                            </td>
                            <td className="py-2 pr-3">
                              {r.soll?.qty !== undefined ? `${r.soll.qty}` : "—"} · {formatUsd(r.soll?.unit_price_usd)}
                              {r.soll ? (
                                <div className="text-xs text-ink-2">Andere Container: {r.soll.other_packed_qty} Einh.</div>
                              ) : null}
                            </td>
                            <td className="py-2 pr-3">
                              {istPositionsIndex(r.positionIndex) && r.doc ? (
                                <div className="space-y-1">
                                {darfZeileAusgelassenWerden || draft?.skip ? (
                                  <label className="flex items-center gap-1 text-xs text-ink-2">
                                    <input
                                      type="checkbox"
                                      aria-label={`Zeile ${r.positionIndex + 1} ${skipBeschriftung}`}
                                      checked={draft?.skip ?? false}
                                      onChange={(event) => setReviewMappings((current) => ({
                                        ...current,
                                        // Ziel und Einheitenentscheidung bleiben lokal erhalten,
                                        // falls der Nutzer das Auslassen wieder zuruecknimmt. Der
                                        // Request sendet sie bei skip=true trotzdem nicht mit.
                                        [r.positionIndex!]: {
                                          target: current[r.positionIndex!]?.target ?? "",
                                          targetTouched: current[r.positionIndex!]?.targetTouched ?? false,
                                          rowFingerprint: aktuellerFingerabdruck,
                                          serverUpdateVisible: current[r.positionIndex!]?.serverUpdateVisible ?? false,
                                          decision: current[r.positionIndex!]?.decision ?? "",
                                          bestaetigterFaktor: current[r.positionIndex!]?.bestaetigterFaktor,
                                          bestaetigtesZiel: current[r.positionIndex!]?.bestaetigtesZiel,
                                          vorbelegt: current[r.positionIndex!]?.vorbelegt,
                                          manualQty: current[r.positionIndex!]?.manualQty ?? "",
                                          skip: event.target.checked,
                                        },
                                      }))}
                                    />
                                    {skipBeschriftung}
                                  </label>
                                ) : null}
                                <select
                                  aria-label={`Grundartikel Zeile ${r.positionIndex + 1}`}
                                  value={angezeigtesZiel}
                                  disabled={draft?.skip ?? false}
                                  onChange={(event) => setReviewMappings((current) => ({
                                    ...current,
                                    [r.positionIndex!]: {
                                      target: event.target.value,
                                      targetTouched: true,
                                      rowFingerprint: aktuellerFingerabdruck,
                                      serverUpdateVisible: false,
                                      decision: current[r.positionIndex!]?.decision ?? "",
                                      manualQty: current[r.positionIndex!]?.manualQty ?? "",
                                      skip: current[r.positionIndex!]?.skip ?? false,
                                    },
                                  }))}
                                  className="max-w-72 rounded border border-line bg-background px-2 py-1 text-xs"
                                >
                                  <option value="">— bitte Grundartikel wählen —</option>
                                  {(reviewData.targets ?? []).map((target) => (
                                    <option key={target.order_item_id} value={target.order_item_id}>
                                      {target.merchant_sku ?? `#${target.order_item_id}`} · {target.name ?? "ohne Bezeichnung"}
                                    </option>
                                  ))}
                                </select>
                                {zielWurdeAktualisiert ? (
                                  <div role="status" aria-live="polite" className="text-xs text-amber-700 dark:text-amber-300">
                                    Zeile oder Zuordnung wurde durch die aktuelle Serverprüfung aktualisiert. Bitte erneut prüfen.
                                  </div>
                                ) : null}
                                </div>
                              ) : "—"}
                            </td>
                            <td className="py-2 pr-3 text-xs">{katLabel}</td>
                          </tr>
                          {hasCartonMismatch && r.doc ? (
                            <tr className="border-b border-red-200 bg-red-50 dark:bg-red-950/30">
                              <td colSpan={6} className="px-3 py-2 text-sm text-red-800 dark:text-red-200">
                                <strong>Kartonmenge prüfen:</strong>{" "}
                                {r.doc.cartons} Kartons × {r.doc.pieces_per_carton} Einh. ergeben{" "}
                                {r.doc.cartons! * r.doc.pieces_per_carton!} Einh.,
                                die Packliste nennt jedoch {r.doc.qty} Einh. Die Abweichung wird nicht automatisch als Überzahlung gewertet.
                              </td>
                            </tr>
                          ) : null}
                          {ohneMenge && !draft?.skip && istPositionsIndex(r.positionIndex) ? (
                            <tr className="border-b border-red-200 bg-red-50 dark:bg-red-950/30">
                              <td colSpan={6} className="px-3 py-2 text-sm text-red-800 dark:text-red-200">
                                Nicht übernehmbar – Menge fehlt im Dokument
                              </td>
                            </tr>
                          ) : null}
                          {needsUnitDecision && !draft?.skip && r.doc && istPositionsIndex(r.positionIndex) ? (
                            <tr className="border-b border-red-200 bg-red-50 dark:bg-red-950/30">
                              <td colSpan={6} className="px-3 py-3">
                                <div className="flex flex-wrap items-center gap-3 text-sm text-red-800 dark:text-red-200">
                                  <strong>{paarZeile ? (paarFaktorFuer(r) === 2 ? "Paare gezählt?" : "Sets gezählt?") : "Einheit prüfen:"}</strong>
                                  {paarZeile && paarGesperrt(r) ? (
                                    <span className="text-xs">
                                      {paarFaktorFuer(r) === 2 ? "Paare" : "Sets"} nicht übernehmbar: {r.doc.paar_frage?.sperre}
                                    </span>
                                  ) : null}
                                  <span>
                                    {r.doc.source_qty ?? "—"} {r.doc.source_unit ?? "Einheit unbekannt"}
                                    {setFaktorFuer(r) !== null && r.doc.source_qty !== null
                                      ? ` × ${setFaktorFuer(r)} = ${r.doc.source_qty * setFaktorFuer(r)!} Stück`
                                      : ""}
                                  </span>
                                  {r.doc.source_unit_price_usd !== null && setFaktorFuer(r) !== null ? (
                                    <span>
                                      {formatUsd(r.doc.source_unit_price_usd)} / {setFaktorFuer(r)} = {formatUsd(
                                        Math.round((r.doc.source_unit_price_usd / setFaktorFuer(r)!) * 10_000) / 10_000,
                                      )} je Stück
                                    </span>
                                  ) : null}
                                  {r.doc.blocked_reason ? <span>{r.doc.blocked_reason}</span> : null}
                                  <select
                                    aria-label={`Einheitenentscheidung Zeile ${r.positionIndex + 1}`}
                                    value={wirksameEntscheidung(r, draft ?? undefined)}
                                    onChange={(event) => setReviewMappings((current) => ({
                                      ...current,
                                    [r.positionIndex!]: {
                                      target: current[r.positionIndex!]?.target ?? "",
                                      targetTouched: current[r.positionIndex!]?.targetTouched ?? false,
                                      rowFingerprint: aktuellerFingerabdruck,
                                      serverUpdateVisible: current[r.positionIndex!]?.serverUpdateVisible ?? false,
                                      decision: event.target.value as "" | "set_faktor" | "already_pieces" | "manual_qty",
                                      ...(event.target.value === "set_faktor"
                                        ? { bestaetigterFaktor: setFaktorFuer(r), bestaetigtesZiel: angezeigtesZiel.trim() }
                                        : {}),
                                        manualQty: current[r.positionIndex!]?.manualQty ?? "",
                                        skip: current[r.positionIndex!]?.skip ?? false,
                                      },
                                    }))}
                                    className="rounded border border-red-300 bg-white px-2 py-1 text-xs text-red-950"
                                  >
                                    <option value="">— Entscheidung erforderlich —</option>
                                    {setFaktorFuer(r) !== null
                                      ? (
                                        <option value="set_faktor">
                                          {paarZeile
                                            ? (setFaktorFuer(r) === 2 ? "Ja, Paare (× 2)" : `Ja, Sets (× ${setFaktorFuer(r)})`)
                                            : `Set = ${setFaktorFuer(r)} Stück bestätigen`}
                                        </option>
                                      )
                                      : null}
                                    <option value="already_pieces">{paarZeile ? "Nein, Stück" : "Menge ist bereits Stück"}</option>
                                    <option value="manual_qty">Stückmenge manuell festlegen</option>
                                  </select>
                                  {draft?.vorbelegt && setBestaetigungGilt(r, draft) ? (
                                    <span className="text-xs">
                                      {draft.vorbelegt.grund === "rechnung"
                                        ? `aus Rechnung bestätigt: Set = ${draft.bestaetigterFaktor} Stück`
                                        : draft.vorbelegt.grund === "beleg"
                                          ? `aus früherem Beleg bestätigt: Set = ${draft.bestaetigterFaktor} Stück`
                                          : `vorbelegt: × ${draft.bestaetigterFaktor} ergibt die Bestellmenge`}
                                      {" "}— änderbar
                                    </span>
                                  ) : null}
                                  {draft?.decision === "manual_qty" ? (
                                    <Input
                                      aria-label={`Manuelle Stückmenge Zeile ${r.positionIndex + 1}`}
                                      inputMode="numeric"
                                      value={draft.manualQty}
                                      onChange={(event) => setReviewMappings((current) => ({
                                        ...current,
                                        [r.positionIndex!]: {
                                          target: current[r.positionIndex!]?.target ?? "",
                                          targetTouched: current[r.positionIndex!]?.targetTouched ?? false,
                                          rowFingerprint: aktuellerFingerabdruck,
                                          serverUpdateVisible: current[r.positionIndex!]?.serverUpdateVisible ?? false,
                                          decision: "manual_qty",
                                          manualQty: event.target.value,
                                          skip: current[r.positionIndex!]?.skip ?? false,
                                        },
                                      }))}
                                      className="h-8 w-32 bg-white"
                                      placeholder="Stück"
                                    />
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>

          {reviewData && !isReviewLoading && !isReviewError ? (
            <DialogFooter className="shrink-0 flex-col border-t border-line bg-background px-6 py-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-2xl space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    ref={reviewTransferRef}
                    type="checkbox"
                    id="uebernehmen-check"
                    checked={uebernehmenChecked}
                    disabled={reviewMussUebernehmen || !reviewData.purchase_shipment_id || !reviewUnterstuetztSichereUebernahme}
                    aria-describedby={!reviewUnterstuetztSichereUebernahme
                      ? "uebernehmen-backend-hinweis"
                      : "uebernehmen-wirkung"}
                    onChange={(e) => setUebernehmenChecked(e.target.checked)}
                    className="rounded border-line"
                  />
                  <label
                    htmlFor="uebernehmen-check"
                    className={`text-xs ${reviewMussUebernehmen || !reviewData.purchase_shipment_id || !reviewUnterstuetztSichereUebernahme ? "text-ink-2 opacity-60 cursor-not-allowed" : "text-ink cursor-pointer"}`}
                  >
                    {reviewMussUebernehmen ? "Positionen werden verbindlich in den Container übernommen" : "Positionen in Container übernehmen"}
                    {!reviewData.purchase_shipment_id && " (kein Container zugeordnet)"}
                  </label>
                </div>
                {reviewUnterstuetztSichereUebernahme ? (
                  <div id="uebernehmen-wirkung" className="text-xs text-ink-2">
                    Übernimmt Stückzahlen, Kartons und Stück je Karton in den zugeordneten Container.
                  </div>
                ) : null}
                {reviewMussUebernehmen && !reviewData.purchase_shipment_id ? (
                  <div id="confirm-container-required" role="alert" className="text-xs text-red-600 dark:text-red-400">
                    Diese Packliste muss zuerst eindeutig einem Container zugeordnet werden.
                  </div>
                ) : null}
                {!reviewMussUebernehmen && !uebernehmenChecked && reviewData.purchase_shipment_id && reviewUnterstuetztSichereUebernahme ? (
                  <div id="confirm-mode-hint" className="text-xs text-amber-700 dark:text-amber-300">
                    Ohne diesen Haken wird nur das Dokument bestätigt; Kartons und Stückzahlen bleiben unverändert.
                  </div>
                ) : null}
                {!reviewUnterstuetztSichereUebernahme ? (
                  <div id="uebernehmen-backend-hinweis" role="alert" className="text-xs text-red-600 dark:text-red-400">
                    Sichere Positionsübernahme nicht verfügbar: Bitte zuerst das Backend aktualisieren.
                  </div>
                ) : null}
                {reviewUnterstuetztSichereUebernahme && reviewMappingResult.error ? (
                  <div role="alert" className="text-xs text-red-600 dark:text-red-400">{reviewMappingResult.error}</div>
                ) : null}
                {uebernehmenChecked && reviewMappingResult.error === null ? (
                  <div id="confirm-mode-ready" role="status" aria-live="polite" className="text-xs text-emerald-700 dark:text-emerald-300">
                    Bereit: {reviewMappingResult.mappings.length} Position(en) werden übernommen
                    {reviewMappingResult.skippedPositionIndices.length > 0
                      ? `; ${reviewMappingResult.skippedPositionIndices.length} Zusatzzeile(n) werden ausgelassen.`
                      : "."}
                  </div>
                ) : null}
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto w-full whitespace-normal text-red-600 dark:text-red-400 sm:w-auto"
                    onClick={() => {
                      setRejectDocId(reviewData.document_id);
                      setRejectReason("");
                    }}
                  >
                    Ablehnen…
                  </Button>
                  <Button
                    size="sm"
                    className="h-auto w-full whitespace-normal sm:w-auto"
                    aria-describedby={reviewMussUebernehmen && !reviewData.purchase_shipment_id
                      ? "confirm-container-required"
                      : (uebernehmenChecked && reviewMappingResult.error === null
                        ? "confirm-mode-ready"
                        : (!uebernehmenChecked
                        && reviewData.purchase_shipment_id
                        && reviewUnterstuetztSichereUebernahme
                          ? "confirm-mode-hint"
                          : undefined))}
                    disabled={confirmDoc.isPending
                      || reviewMappingResult.error !== null
                      || (reviewMussUebernehmen && (!reviewData.purchase_shipment_id || !reviewUnterstuetztSichereUebernahme))}
                    onClick={() => {
                      confirmDoc.mutate(
                        {
                          docId: reviewData.document_id,
                          reviewVersion: reviewData.review_version,
                          uebernehmen: uebernehmenChecked,
                          mappings: uebernehmenChecked ? reviewMappingResult.mappings : undefined,
                          skippedPositionIndices: uebernehmenChecked
                            ? reviewMappingResult.skippedPositionIndices
                            : undefined,
                          aggregationConfirmations: uebernehmenChecked
                            ? reviewMappingResult.aggregationConfirmations
                            : undefined,
                        },
                        {
                          onSuccess: (antwort) => {
                            // Der Server unterscheidet geaenderte von wirkungslosen Zeilen —
                            // "0 übernommen" ohne Erklaerung liest sich sonst wie ein Fehler.
                            const unveraendert = antwort?.unveraendert ?? 0;
                            if (uebernehmenChecked && antwort?.uebernommen === 0 && unveraendert > 0) {
                              toast.success(
                                `Dokument bestätigt – ${unveraendert} Position(en) unverändert, nichts zu ändern`,
                              );
                            } else if (uebernehmenChecked && unveraendert > 0) {
                              toast.success(
                                `Dokument bestätigt – ${antwort.uebernommen} übernommen, ${unveraendert} unverändert`,
                              );
                            } else {
                              toast.success("Dokument bestätigt");
                            }
                            setReviewDocId(null);
                          },
                          onError: err("Bestätigung fehlgeschlagen"),
                        }
                      );
                    }}
                  >
                    Bestätigen
                  </Button>
                </div>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDocId !== null} onOpenChange={(open) => { if (!open) setRejectDocId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dokument ablehnen</DialogTitle>
            <DialogDescription>
              Bitte gib einen Grund für die Ablehnung des Dokuments an.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder="Grund für Ablehnung (z. B. falsche Summen, unlesbar...)"
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
            <div className="text-right text-xs text-ink-2">
              {rejectReason.length}/500
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejectDocId(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={rejectDoc.isPending || !rejectReason.trim()}
              onClick={() => {
                if (!rejectDocId) return;
                rejectDoc.mutate(
                  { docId: rejectDocId, grund: rejectReason.trim() },
                  {
                    onSuccess: () => {
                      toast.success("Dokument abgelehnt");
                      setRejectDocId(null);
                      if (reviewDocId === rejectDocId) setReviewDocId(null);
                    },
                    onError: () => toast.error("Ablehnung fehlgeschlagen"),
                  }
                );
              }}
            >
              Dokument ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDocId !== null} onOpenChange={(open) => { if (!open) setDeleteDocId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dokument löschen</DialogTitle>
            <DialogDescription>
              {dokumentLoeschHinweis(deleteTargetDoc?.sharepoint_path ?? null)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              placeholder="Grund für Löschung (z. B. versehentlich hochgeladen)"
              maxLength={500}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={4}
            />
            <div className="text-right text-xs text-ink-2">
              {deleteReason.length}/500
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteDocId(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={deleteDoc.isPending || !deleteReason.trim()}
              onClick={() => {
                if (!deleteDocId) return;
                deleteDoc.mutate(
                  { docId: deleteDocId, grund: deleteReason.trim() },
                  {
                    onSuccess: () => {
                      toast.success("Dokument gelöscht");
                      setDeleteDocId(null);
                      if (reviewDocId === deleteDocId) setReviewDocId(null);
                    },
                    onError: err("Löschen fehlgeschlagen"),
                  }
                );
              }}
            >
              Dokument löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={correctionDialog !== null} onOpenChange={(open) => { if (!open) setCorrectionDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{correctionDialog?.kind === "reopen_document"
              ? "Packliste korrigieren"
              : correctionDialog?.kind === "clear_carton_exception"
                ? "Packdaten-Ausnahme aufheben"
                : "Packdaten-Ausnahme begründen"}</DialogTitle>
            <DialogDescription>
              {correctionDialog?.kind === "reopen_document"
                ? "Die bestätigte Packliste wird vor der Zustellung wieder zur Prüfung geöffnet. Ihre bisherigen Containerwerte aus diesem Dokument werden entfernt."
                : correctionDialog?.kind === "clear_carton_exception"
                  ? `Die protokollierte Ausnahme für ${correctionTargetLabel ?? "diese Position"} wird aufgehoben. Danach müssen die Packdaten wieder vollständig und widerspruchsfrei sein.`
                  : `Nur wenn Kartons oder Stück je Karton für ${correctionTargetLabel ?? "diese Position"} fachlich nicht verfügbar sind, darf die Zustellung mit einer protokollierten Ausnahme fortgesetzt werden.`}
            </DialogDescription>
          </DialogHeader>
          {correctionDialog?.kind === "clear_carton_exception" ? (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
              Bisherige Begründung: {correctionShipmentItem?.carton_exception_reason ?? "—"}
            </div>
          ) : (
            <Textarea
              autoFocus
              aria-label="Begründung der Korrektur"
              placeholder="Begründung eingeben"
              value={correctionReason}
              maxLength={500}
              onChange={(event) => setCorrectionReason(event.target.value)}
            />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectionDialog(null)}>Abbrechen</Button>
            <Button
              variant={correctionDialog?.kind === "clear_carton_exception" ? "destructive" : "default"}
              disabled={reopenDoc.isPending || setCartonException.isPending
                || (correctionDialog?.kind !== "clear_carton_exception" && correctionReason.trim().length < 3)}
              onClick={() => {
                if (!correctionDialog) return;
                const onSuccess = () => {
                  toast.success(correctionDialog.kind === "reopen_document"
                    ? "Packliste erneut zur Prüfung geöffnet"
                    : correctionDialog.kind === "clear_carton_exception"
                      ? "Packdaten-Ausnahme aufgehoben"
                      : "Begründete Packdaten-Ausnahme gespeichert");
                  setCorrectionDialog(null);
                };
                if (correctionDialog.kind === "reopen_document") {
                  reopenDoc.mutate(
                    { docId: correctionDialog.id, grund: correctionReason.trim() },
                    { onSuccess, onError: err("Packliste konnte nicht geöffnet werden") },
                  );
                } else if (correctionDialog.kind === "carton_exception") {
                  setCartonException.mutate(
                    { id: correctionDialog.id, grund: correctionReason.trim() },
                    { onSuccess, onError: err("Ausnahme konnte nicht gespeichert werden") },
                  );
                } else {
                  setCartonException.mutate(
                    { id: correctionDialog.id, grund: null },
                    { onSuccess, onError: err("Ausnahme konnte nicht aufgehoben werden") },
                  );
                }
              }}
            >
              {correctionDialog?.kind === "clear_carton_exception" ? "Ausnahme aufheben" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{order.archived_at ? "Bestellung wiederherstellen" : "Bestellung archivieren"}</DialogTitle>
            <DialogDescription>
              {order.archived_at
                ? "Die Bestellung erscheint danach wieder in der Einkaufsübersicht. Die Historie bleibt vollständig erhalten."
                : "Die Bestellung wird aus der aktiven Einkaufsübersicht ausgeblendet, aber nicht gelöscht. Alle Daten und die Historie bleiben erhalten."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            aria-label="Begründung für Archivierung oder Wiederherstellung"
            placeholder={order.archived_at ? "Warum wird die Bestellung wiederhergestellt?" : "Warum wird die Bestellung archiviert?"}
            value={archiveReason}
            maxLength={500}
            onChange={(event) => setArchiveReason(event.target.value)}
          />
          {!order.archived_at && canArchiveAltbestand ? (
            <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={archiveAltbestand}
                disabled={archiveOrder.isPending}
                aria-describedby="archive-altbestand-hint"
                onChange={(event) => { setArchiveWeOffen(false); setArchiveAltbestand(event.target.checked); }}
              />
              <span>
                <span className="font-semibold">Altbestand (manuell geführt) – archivieren trotz offenem Status</span>
                <span id="archive-altbestand-hint" className="mt-1 block">
                  Die Bestellung verschwindet aus den Plattform-Abläufen; ihr Status bleibt unverändert. Sie kann jederzeit begründet wiederhergestellt werden.
                </span>
              </span>
            </label>
          ) : null}
          {archiveWeOffen ? (
            <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-700/50 dark:bg-red-950/20 dark:text-red-200">
              Ein Wareneingang ist noch offen (oder ein Container ist zugestellt, aber ohne Wareneingang). Bitte den Wareneingang erst abschließen, dann als Altbestand archivieren.
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>Abbrechen</Button>
            <Button
              variant={order.archived_at ? "default" : "destructive"}
              disabled={archiveOrder.isPending || restoreOrder.isPending || archiveReason.trim().length < 3}
              onClick={() => {
                const grund = archiveReason.trim();
                const onSuccess = () => {
                  toast.success(order.archived_at ? "Bestellung wiederhergestellt" : "Bestellung archiviert");
                  setArchiveDialogOpen(false);
                };
                if (order.archived_at) {
                  restoreOrder.mutate(grund, { onSuccess, onError: err("Wiederherstellung fehlgeschlagen") });
                  return;
                }
                setArchiveWeOffen(false);
                const altbestand = archiveAltbestand && canArchiveAltbestand;
                archiveOrder.mutate(altbestand ? { grund, altbestand: true } : { grund }, {
                  onSuccess,
                  onError: (e) => {
                    if (e instanceof ApiError && e.details.code === "we_offen") setArchiveWeOffen(true);
                    else err("Archivierung fehlgeschlagen")(e);
                  },
                });
              }}
            >
              {order.archived_at ? "Wiederherstellen" : "Archivieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={purgeDialogOpen}
        onOpenChange={(open) => { if (!purgeOrder.isPending) setPurgeDialogOpen(open); }}
      >
        <DialogContent
          className="max-h-[90vh] max-w-2xl overflow-y-auto"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            purgeTitleRef.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle ref={purgeTitleRef} tabIndex={-1}>Stornierte Bestellung endgueltig loeschen</DialogTitle>
            <DialogDescription>
              Nur der Bestellgraph in STAHLIA wird entfernt. SharePoint-Originale bleiben erhalten;
              ihr Hash wird fuer automatische Wiederanlage dauerhaft gesperrt.
            </DialogDescription>
          </DialogHeader>

          {purgeError ? (
            <div
              ref={purgeErrorRef}
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
            >
              {purgeError}
            </div>
          ) : null}

          {purgePreview.isLoading ? (
            <div role="status" className="rounded-md border border-line p-3 text-sm">Loeschvorschau wird geprueft …</div>
          ) : purgePreview.isError || !purgePreview.data ? (
            <div role="alert" aria-live="assertive" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              Die Loeschvorschau konnte nicht geladen werden. Es wurde nichts veraendert.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-line bg-muted/30 p-3 text-sm">
                <div className="font-semibold">Vorschau fuer {purgePreview.data.order_number}</div>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                  {Object.entries(purgePreview.data.counts).map(([name, count]) => (
                    <Fragment key={name}>
                      <dt className="text-ink-2">{name.replaceAll("_", " ")}</dt>
                      <dd className="font-medium sm:col-span-2">{count}</dd>
                    </Fragment>
                  ))}
                  <dt className="text-ink-2">Dokumenthashes</dt><dd className="font-medium sm:col-span-2">{purgePreview.data.document_hash_count}</dd>
                  <dt className="text-ink-2">Vormerkungen bleiben erhalten</dt><dd className="font-medium sm:col-span-2">{purgePreview.data.retained_purchase_notes}</dd>
                </dl>
              </div>

              {!purgePreview.data.can_purge ? (
                <div role="alert" className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-semibold">Loeschung ist gesperrt</div>
                  <ul className="mt-1 list-disc pl-5">
                    {purgePreview.data.blockers.map((blocker) => <li key={blocker}>{blocker.replaceAll("_", " ")}</li>)}
                  </ul>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label htmlFor="purge-reason" className="text-sm font-medium">Fester Loeschgrund</label>
                    <select
                      id="purge-reason"
                      className="w-full rounded border border-line bg-background px-3 py-2 text-sm"
                      value={purgeReason}
                      disabled={purgeOrder.isPending}
                      onChange={(event) => { setPurgeError(null); setPurgeReason(event.target.value as PurchasePurgeReasonCode); }}
                    >
                      <option value="duplicate_order">Doppelt angelegte Bestellung</option>
                      <option value="erroneous_import">Fehlerhafter Import</option>
                      <option value="replacement_order">Durch neue Bestellung ersetzt</option>
                      <option value="test_order">Testbestellung</option>
                      <option value="other_admin_cleanup">Sonstige administrative Bereinigung</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="purge-order-number" className="text-sm font-medium">
                      Bestellnummer exakt eingeben: <span className="font-mono">{purgePreview.data.order_number}</span>
                    </label>
                    <Input
                      id="purge-order-number"
                      autoComplete="off"
                      value={purgeOrderNumber}
                      disabled={purgeOrder.isPending}
                      onChange={(event) => { setPurgeError(null); setPurgeOrderNumber(event.target.value); }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="purge-pin" className="text-sm font-medium">Persoenliche sechsstellige PIN</label>
                    <Input
                      id="purge-pin"
                      type="password"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={6}
                      value={purgePin}
                      disabled={purgeOrder.isPending}
                      onChange={(event) => { setPurgeError(null); setPurgePin(event.target.value.replace(/\D/g, "").slice(0, 6)); }}
                    />
                  </div>
                  <label className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={purgeIrreversible}
                      disabled={purgeOrder.isPending}
                      onChange={(event) => { setPurgeError(null); setPurgeIrreversible(event.target.checked); }}
                    />
                    <span>Ich verstehe, dass die Bestellung in STAHLIA nicht wiederhergestellt werden kann.</span>
                  </label>
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={purgeOrder.isPending} onClick={() => setPurgeDialogOpen(false)}>Abbrechen</Button>
            <Button
              variant="destructive"
              disabled={!purgePreview.data?.can_purge || purgeOrder.isPending
                || purgeOrderNumber !== purgePreview.data?.order_number || !/^\d{6}$/.test(purgePin) || !purgeIrreversible}
              onClick={() => {
                const preview = purgePreview.data;
                if (!preview) return;
                purgeOrder.mutate({
                  order_number_confirmation: purgeOrderNumber,
                  pin: purgePin,
                  manifest_sha256: preview.manifest_sha256,
                  idempotency_key: purgeIdempotencyKey,
                  reason_code: purgeReason,
                  irreversible_confirmation: true,
                }, {
                  onSuccess: () => {
                    setPurgeError(null);
                    toast.success(`Bestellung ${preview.order_number} wurde endgueltig geloescht`);
                    setPurgeDialogOpen(false);
                    navigate("/einkauf", { replace: true });
                  },
                  onError: (error) => {
                    const message = fehlerText(error, "Bestellung konnte nicht sicher geloescht werden");
                    setPurgeError(message);
                    toast.error(message);
                    window.setTimeout(() => purgeErrorRef.current?.focus(), 0);
                    // Bei einem Transportfehler ist der Serverausgang unbekannt. Ein
                    // technischer Retry muss deshalb exakt denselben Idempotency-Key
                    // und dieselbe bestaetigte Absicht wiederverwenden. Nur eine klar
                    // beantwortete fachliche Ablehnung verwirft die Absicht und holt
                    // eine frische Vorschau.
                    const activeReplay = error instanceof ApiError
                      && error.status === 409
                      && error.message.includes("Bestellung wird gerade bearbeitet");
                    if (isTransportError(error) || activeReplay) return;
                    setPurgeOrderNumber("");
                    setPurgePin("");
                    setPurgeIrreversible(false);
                    setPurgeIdempotencyKey(crypto.randomUUID());
                    void purgePreview.refetch();
                  },
                });
              }}
            >
              {purgeOrder.isPending ? "Loeschung wird geprueft …" : "Bestellung endgueltig loeschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteShipmentId !== null} onOpenChange={(open) => { if (!open) setDeleteShipmentId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Container löschen</DialogTitle>
            <DialogDescription>
              Zugeordnete Dokumente werden wieder freigegeben und Containerpositionen entfernt. Zahlungen, Kosten oder ein Wareneingang blockieren das Löschen.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            aria-label="Begründung für das Löschen"
            placeholder="Warum wird dieser Container entfernt?"
            value={deleteShipmentReason}
            maxLength={500}
            onChange={(event) => setDeleteShipmentReason(event.target.value)}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteShipmentId(null)}>Abbrechen</Button>
            <Button
              variant="destructive"
              disabled={deleteShipment.isPending || deleteShipmentReason.trim().length < 3}
              onClick={() => {
                if (deleteShipmentId === null) return;
                deleteShipment.mutate(
                  { id: deleteShipmentId, grund: deleteShipmentReason.trim() },
                  {
                    onSuccess: (result) => {
                      toast.success(`Container gelöscht; ${result.unassignedDocuments ?? 0} Dokument(e) wieder freigegeben.`);
                      const deletedId = deleteShipmentId;
                      if (uploadShipmentId === String(deletedId)) setUploadShipmentId("order");
                      if (reviewShipmentId === String(deletedId)) setReviewShipmentId("");
                      if (weDialogShipmentId === deletedId) setWeDialogShipmentId(null);
                      if (startmengeShipmentId === deletedId) setStartmengeShipmentId(null);
                      if (finalShipmentId === deletedId) setFinalShipmentId(null);
                      if (mischEkShipmentId === deletedId) setMischEkShipmentId(null);
                      if (bestandBuchenShipmentId === deletedId) setBestandBuchenShipmentId(null);
                      setDeleteShipmentId(null);
                    },
                    onError: err("Container konnte nicht gelöscht werden"),
                  },
                );
              }}
            >
              Container löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kombinierte Zustellbestaetigung mit Positions-Vorschau vor der WE-Anlage. */}
      <Dialog open={weDialogShipmentId !== null} onOpenChange={(open) => { if (!open) setWeDialogShipmentId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{weDialogIstReparatur
              ? "Fehlenden Wareneingang sicher anlegen"
              : "Zustellung bestätigen und Wareneingang anlegen"}</DialogTitle>
            <DialogDescription>
              {weDialogIstReparatur
                ? `${weDialogShipment?.label ?? `Container #${weDialogShipmentId}`} ist bereits als „Im Wareneingang“ markiert. Mit der Bestätigung wird ausschließlich die fehlende WE-Verknüpfung idempotent angelegt.`
                : `${weDialogShipment?.label ?? `Container #${weDialogShipmentId}`} ist physisch bei uns angekommen. Mit der Bestätigung werden Zustellung und vorbefüllter Wareneingang atomar angelegt.`}
            </DialogDescription>
          </DialogHeader>

          {!weDialogShipment ? (
            <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700/50 dark:bg-red-950/20 dark:text-red-200">
              Der Container ist nicht mehr vorhanden oder wurde zwischenzeitlich geändert. Bitte den Dialog schließen und die neu geladenen Daten prüfen.
            </div>
          ) : weDialogShipment.items.length > 0 ? (
            <div className="max-h-[50vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs text-ink-2">
                    <th className="py-2 pr-3">Sternnummer</th>
                    <th className="py-2 pr-3 text-right">Menge</th>
                    <th className="py-2 pr-3">Kartons</th>
                  </tr>
                </thead>
                <tbody>
                  {weDialogShipment.items.map((si) => {
                    const stern = order.items.find((it) => it.id === si.purchase_order_item_id)?.merchant_sku ?? "—";
                    return (
                      <tr key={si.id} className="border-b border-line/50">
                        <td className="py-2 pr-3 font-medium">
                          {si.purchase_order_item_id === null ? (
                            <span className="inline-flex items-center rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
                              Ohne Zuordnung
                            </span>
                          ) : (
                            stern
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right">{si.qty.toLocaleString("de-DE")}</td>
                        <td className="py-2 pr-3">
                          {si.cartons == null ? (
                            <span className="inline-flex items-center rounded bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                              Packdaten fehlen
                            </span>
                          ) : (
                            <span>{si.cartons.toLocaleString("de-DE")} × {si.pieces_per_carton ?? "?"}</span>
                          )}
                          {si.carton_exception_reason ? (
                            <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">Ausnahme: {si.carton_exception_reason}</div>
                          ) : null}
                          {si.carton_exception_reason ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-1 h-7 text-[11px]"
                              disabled={!hasArticlePurchase || setCartonException.isPending}
                              aria-label={`Packdaten-Ausnahme für ${stern} aufheben`}
                              onClick={() => {
                                setCorrectionReason("");
                                setCorrectionDialog({ kind: "clear_carton_exception", id: si.id });
                              }}
                            >
                              Ausnahme aufheben
                            </Button>
                          ) : si.cartons == null || si.pieces_per_carton == null ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="mt-1 h-7 text-[11px]"
                              disabled={!hasArticlePurchase || setCartonException.isPending}
                              aria-label={`Packdaten-Ausnahme für ${stern} begründen`}
                              onClick={() => {
                                setCorrectionReason("");
                                setCorrectionDialog({ kind: "carton_exception", id: si.id });
                              }}
                            >
                              Ausnahme begründen
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700/50 dark:bg-red-950/20 dark:text-red-200">
              Dieser Container hat noch keine bestätigten Positionen. Bitte zuerst eine Packliste hochladen, prüfen und in diesen Container übernehmen. {weDialogIstReparatur ? "Der fehlende Wareneingang wird vorher nicht angelegt." : "Die Zustellung wird vorher nicht gespeichert."}
            </div>
          )}

          {weDialogShipment && !weDialogIstReparatur ? (
            <ContainerAbgleichFeld
              wert={containerEingabe}
              onWert={(wert) => setContainerAbgleich({ shipmentId: weDialogShipment.id, wert })}
              soll={weDialogShipment.container_no}
              andere={order.shipments.filter((s) => s.id !== weDialogShipment.id)}
              onWechseln={(id) => {
                setContainerAbgleich({ shipmentId: id, wert: containerEingabe });
                setWeDialogShipmentId(id);
              }}
            />
          ) : null}

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeDialogShipmentId(null)}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              aria-describedby={!weDialogKannBestaetigen && weDialogShipment ? "we-confirm-blocker" : undefined}
              disabled={confirmDelivery.isPending || !weDialogShipment || !weDialogKannBestaetigen}
              onClick={() => {
                if (weDialogShipmentId === null) return;
                if (shipmentActionRef.current.has(weDialogShipmentId)) return;
                const shipmentId = weDialogShipmentId;
                shipmentActionRef.current.add(shipmentId);
                // Leere Eingabe = Zustellung wie bisher; der Abgleich ist nur Hinweis (P3).
                const abgleich = !weDialogIstReparatur && normalisiereContainerNr(containerEingabe) !== null
                  ? { container_abgleich: { eingegeben: containerEingabe } }
                  : undefined;
                confirmDelivery.mutate(abgleich, {
                  onSuccess: (res) => {
                    if (res.outcome === "receipt_ready") {
                      setDeliveryHinweise((current) => {
                        const next = { ...current };
                        delete next[shipmentId];
                        return next;
                      });
                      toast.success(zustellErgebnisText(res));
                    } else {
                      const hinweis = zustellErgebnisText(res);
                      setDeliveryHinweise((current) => ({ ...current, [shipmentId]: hinweis }));
                      toast.warning(hinweis);
                    }
                    setWeDialogShipmentId(null);
                  },
                  onError: (error) => {
                    if (error instanceof ApiError && error.status === 404) {
                      // 404 ist mehrdeutig: altes Backend ODER ein zwischenzeitlich
                      // entfernter Container. Neu laden und keine falsche Ursache behaupten.
                      void refetchOrder();
                      toast.error("Zustellaktion nicht verfügbar oder Container nicht mehr vorhanden. Daten werden neu geladen.");
                      return;
                    }
                    toast.error(fehlerText(error, "Zustellung konnte nicht bestätigt werden"));
                  },
                  onSettled: () => shipmentActionRef.current.delete(shipmentId),
                });
              }}
            >
              {weDialogIstReparatur ? "Fehlenden Wareneingang anlegen" : "Jetzt Zustellung und Wareneingang bestätigen"}
            </Button>
            {!weDialogKannBestaetigen && weDialogShipment ? (
              <span id="we-confirm-blocker" className="text-xs text-red-600 dark:text-red-400">Packliste, Packdaten und serverseitiger Workflow müssen vollständig freigegeben sein.</span>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StartmengeDialog
        shipmentId={startmengeShipmentId}
        onClose={() => setStartmengeShipmentId(null)}
      />
      <FinalPushDialog
        shipmentId={finalShipmentId}
        shipmentStatus={order.shipments.find((s) => s.id === finalShipmentId)?.status ?? null}
        onClose={() => setFinalShipmentId(null)}
      />
      <BestandBuchenDialog
        shipmentId={bestandBuchenShipmentId}
        order={order}
        onClose={() => setBestandBuchenShipmentId(null)}
      />

      <MischEkDialog
        shipmentId={mischEkShipmentId}
        reihenfolgeOffen={mischEkReihenfolgeOffen(order, mischEkShipmentId)}
        onClose={() => setMischEkShipmentId(null)}
      />
    </div>
  );
}

/** Mehrere Bestellpositionen desselben Artikels im Container: eine Zeile, ein DR-Write je Artikel. */
function PositionenHinweis({ anzahl }: { anzahl: number }) {
  if (anzahl <= 1) return null;
  return (
    <span
      className="ml-1.5 inline-flex items-center rounded bg-panel-2 px-1.5 py-0.5 text-[10px] font-medium text-ink-2"
      title="Mehrere Bestellpositionen desselben Artikels — zusammengefasst, ein Wert je Artikel"
    >
      {anzahl} Positionen
    </span>
  );
}

function formatUsdPreis(preis: number | null, mittel: boolean): string {
  if (preis === null) return "—";
  const text = `${preis.toLocaleString("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} $`;
  return mittel ? `Ø ${text}` : text;
}

function StartmengeDialog({
  shipmentId,
  onClose,
}: {
  shipmentId: number | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useStartmengeKandidaten(shipmentId);
  const pushStartmenge = usePushStartmenge(shipmentId ?? 0);

  const [inputQtys, setInputQtys] = useState<Record<number, number>>({});
  const [results, setResults] = useState<PushResultItem[] | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [gateClosed, setGateClosed] = useState(false);

  const kandidaten = data?.kandidaten ?? [];

  useEffect(() => {
    if (data?.kandidaten) {
      const initial: Record<number, number> = {};
      for (const k of data.kandidaten) {
        initial[k.stockItemId] = k.vorschlag;
      }
      setInputQtys(initial);
    }
  }, [data]);

  const handleConfirmPin = async (pin: string) => {
    if (!shipmentId) return;
    setPinError(null);
    setGateClosed(false);
    const items = kandidaten.map((k) => ({
      stock_item_id: k.stockItemId,
      qty: inputQtys[k.stockItemId] ?? k.vorschlag,
    }));
    try {
      const res = await pushStartmenge.mutateAsync({ pin, items });
      setResults(res.results);
      setPinOpen(false);
      const failed = res.results.filter((r) => !r.ok).length;
      if (failed === 0) toast.success("Startmenge erfolgreich gepusht");
      else toast.warning(`${failed} von ${res.results.length} Artikeln fehlgeschlagen — Details in der Ergebnisliste`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setGateClosed(true);
          setPinOpen(false);
          return;
        }
        setPinError(err.message);
      } else {
        setPinError("Fehler beim Übertragen");
      }
    }
  };

  return (
    <>
      <Dialog open={shipmentId !== null} onOpenChange={(open) => { if (!open) { setResults(null); onClose(); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Null-Artikel-Schnellstart</DialogTitle>
            <DialogDescription>
              Startmengen für Positionen mit Plattform-Bestand ≤ 0 in DR eintragen.
            </DialogDescription>
          </DialogHeader>

          {gateClosed ? (
            <div className="rounded-md border border-zinc-300 bg-zinc-100 p-3 text-xs text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300 my-2">
              DR-Push ist deaktiviert (PURCHASE_DR_PUSH_ENABLED) — Aktivierung über den Koordinator.
            </div>
          ) : null}

          {isLoading ? (
            <div className="py-6 text-center text-sm text-ink-2">Kandidaten laden…</div>
          ) : isError ? (
            <div className="py-6 text-center text-sm text-coral">Fehler beim Laden der Kandidaten.</div>
          ) : kandidaten.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-2">Keine Startmengen-Kandidaten vorhanden.</div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">Sternnummer</th>
                      <th className="py-2 pr-3 text-right">Plattform-Bestand</th>
                      <th className="py-2 pr-3 text-right">Container-Menge</th>
                      <th className="py-2 pr-3 text-right">Startmenge</th>
                      {results && <th className="py-2 pr-3">Ergebnis</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {kandidaten.map((k) => {
                      const resItem = results?.find((r) => r.stockItemId === k.stockItemId);
                      const isRed = k.platformQty <= 0;
                      return (
                        <tr key={k.stockItemId} className="border-b border-line/50">
                          <td className="py-2 pr-3 font-medium">
                            <GrundartikelLink code={k.merchantSku} />
                            <PositionenHinweis anzahl={k.orderItemIds.length} />
                          </td>
                          <td className={`py-2 pr-3 text-right ${isRed ? "text-red-600 dark:text-red-400 font-semibold" : ""}`}>
                            {k.platformQty}
                          </td>
                          <td className="py-2 pr-3 text-right">{k.containerQty}</td>
                          <td className="py-2 pr-3 text-right">
                            <Input
                              type="number"
                              min={0}
                              value={inputQtys[k.stockItemId] ?? k.vorschlag}
                              disabled={!!results || pushStartmenge.isPending}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setInputQtys((prev) => ({
                                  ...prev,
                                  [k.stockItemId]: isNaN(val) ? 0 : val,
                                }));
                              }}
                              className="h-8 w-24 text-right text-xs ml-auto"
                            />
                          </td>
                          {results && (
                            <td className="py-2 pr-3 text-xs">
                              {resItem ? (
                                <span className={resItem.ok ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                  {resItem.ok ? "✓ " : "✗ "}
                                  {pushErgebnisLabel(resItem, inputQtys[k.stockItemId] ?? k.vorschlag)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => { setResults(null); onClose(); }}>
                  {results ? "Schließen" : "Abbrechen"}
                </Button>
                {!results && (
                  <Button
                    size="sm"
                    disabled={pushStartmenge.isPending || kandidaten.length === 0}
                    onClick={() => setPinOpen(true)}
                  >
                    Startmenge pushen…
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PinConfirmDialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        titel="Startmenge freigeben"
        beschreibung="Bitte 6-stellige PIN eingeben, um den DR-Push auszuführen."
        busy={pushStartmenge.isPending}
        errorText={pinError}
        onConfirm={handleConfirmPin}
      />
    </>
  );
}

function FinalPushDialog({
  shipmentId,
  shipmentStatus,
  onClose,
}: {
  shipmentId: number | null;
  shipmentStatus: string | null;
  onClose: () => void;
}) {
  // Doppelzaehl-Schutz (§8): Push nur aus im_wareneingang — bei 'eingelagert'
  // bleibt der Dialog reine Rechnungs-Ansicht (Backend antwortet ohnehin 409).
  const pushErlaubt = shipmentStatus === "im_wareneingang";
  const { data, isLoading, isError } = useEinlagerung(shipmentId);
  const pushFinal = usePushFinal(shipmentId ?? 0);

  const [inputQtys, setInputQtys] = useState<Record<number, number>>({});
  const [results, setResults] = useState<PushResultItem[] | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [gateClosed, setGateClosed] = useState(false);

  const rechnung = data?.rechnung ?? [];
  const packstationOffen = data?.packstationOffen ?? [];
  const packstationHinweis = `Erst Packstation im Wareneingang bestätigen: ${packstationOffen
    .map((item) => `${item.merchantSku} (${item.divertedCartons} Kartons)`).join(", ")}`;

  useEffect(() => {
    if (data?.rechnung) {
      const initial: Record<number, number> = {};
      for (const r of data.rechnung) {
        initial[r.stockItemId] = r.vorschlag;
      }
      setInputQtys(initial);
    }
  }, [data]);

  const handleConfirmPin = async (pin: string) => {
    if (!shipmentId) return;
    setPinError(null);
    setGateClosed(false);
    // Positionen ohne DR-Verknuepfung sind nicht pushbar — nicht mitsenden,
    // sonst verhindert ihr ok:false-Result den Statuswechsel zu 'eingelagert'.
    if (packstationOffen.length > 0) {
      setPinError(packstationHinweis);
      return;
    }
    const items = rechnung
      .filter((r) => r.drStockId !== null)
      .map((r) => ({
        stock_item_id: r.stockItemId,
        qty: inputQtys[r.stockItemId] ?? r.vorschlag,
      }));
    if (items.length === 0) {
      setPinError("Keine pushbare Position (kein DR-Artikel verknüpft)");
      return;
    }
    try {
      const res = await pushFinal.mutateAsync({ pin, items });
      setResults(res.results);
      setPinOpen(false);
      const failed = res.results.filter((r) => !r.ok).length;
      if (failed === 0) toast.success("Einlagerungs-Mengen erfolgreich gepusht");
      else toast.warning(`${failed} von ${res.results.length} Artikeln fehlgeschlagen — Details in der Ergebnisliste`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setGateClosed(true);
          setPinOpen(false);
          return;
        }
        setPinError(err.message);
      } else {
        setPinError("Fehler beim Übertragen");
      }
    }
  };

  return (
    <>
      <Dialog open={shipmentId !== null} onOpenChange={(open) => { if (!open) { setResults(null); onClose(); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Einlagerung prüfen & final pushen</DialogTitle>
            <DialogDescription>
              Transparente Berechnung je Artikel auf Basis der tatsächlich eingelagerten Mengen.
            </DialogDescription>
          </DialogHeader>

          {gateClosed ? (
            <div className="rounded-md border border-zinc-300 bg-zinc-100 p-3 text-xs text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-300 my-2">
              DR-Push ist deaktiviert (PURCHASE_DR_PUSH_ENABLED) — Aktivierung über den Koordinator.
            </div>
          ) : null}

          {packstationOffen.length > 0 && (
            <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
              {packstationHinweis}
            </div>
          )}

          {isLoading ? (
            <div className="py-6 text-center text-sm text-ink-2">Einlagerung laden…</div>
          ) : isError ? (
            <div className="py-6 text-center text-sm text-coral">Fehler beim Laden der Einlagerung.</div>
          ) : rechnung.length === 0 ? (
            <div className="py-6 text-center text-sm text-ink-2">Keine Positionen für Einlagerung vorhanden.</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-line bg-panel-2 p-2.5 text-xs text-ink-2">
                DR-Ist enthält zwischenzeitliche Verkäufe (Pull-Sync)
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">Sternnummer</th>
                      <th className="py-2 pr-3 text-right">Eingelagert</th>
                      <th className="py-2 pr-3 text-right">Vorab gepusht</th>
                      <th className="py-2 pr-3 text-right">DR-Ist</th>
                      <th className="py-2 pr-3 text-right">Vorschlag (Final-Push)</th>
                      {results && <th className="py-2 pr-3">Ergebnis</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rechnung.map((r) => {
                      const resItem = results?.find((res) => res.stockItemId === r.stockItemId);
                      const nichtPushbar = r.drStockId === null;
                      return (
                        <tr key={r.stockItemId} className={`border-b border-line/50 ${nichtPushbar ? "opacity-60" : ""}`}>
                          <td className="py-2 pr-3 font-medium">
                            <GrundartikelLink code={r.merchantSku} />
                            <PositionenHinweis anzahl={r.orderItemIds.length} />
                            {nichtPushbar && (
                              <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-500" title="Kein DR-Artikel verknüpft — wird nicht gepusht">
                                nicht pushbar
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right">{r.eingelagert}</td>
                          <td className="py-2 pr-3 text-right">{r.vorabGepusht}</td>
                          <td className="py-2 pr-3 text-right">{r.drIst}</td>
                          <td className="py-2 pr-3 text-right">
                            <Input
                              type="number"
                              min={0}
                              value={inputQtys[r.stockItemId] ?? r.vorschlag}
                              disabled={!!results || pushFinal.isPending || nichtPushbar || !pushErlaubt}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setInputQtys((prev) => ({
                                  ...prev,
                                  [r.stockItemId]: isNaN(val) ? 0 : val,
                                }));
                              }}
                              className="h-8 w-24 text-right text-xs ml-auto"
                            />
                          </td>
                          {results && (
                            <td className="py-2 pr-3 text-xs">
                              {resItem ? (
                                <span className={resItem.ok ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                  {resItem.ok ? "✓ " : "✗ "}
                                  {pushErgebnisLabel(resItem, inputQtys[r.stockItemId] ?? r.vorschlag)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => { setResults(null); onClose(); }}>
                  {results ? "Schließen" : "Abbrechen"}
                </Button>
                {!results && (
                  <Button
                    size="sm"
                    disabled={pushFinal.isPending || rechnung.length === 0 || !pushErlaubt || packstationOffen.length > 0}
                    title={!pushErlaubt ? "Final-Push nur aus im_wareneingang — Container ist bereits eingelagert" : undefined}
                    onClick={() => setPinOpen(true)}
                  >
                    {pushErlaubt ? "Final-Push ausführen…" : "Bereits eingelagert (nur Ansicht)"}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PinConfirmDialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        titel="Einlagerung final freigeben"
        beschreibung="Bitte 6-stellige PIN eingeben, um den Final-Push auszuführen."
        busy={pushFinal.isPending}
        errorText={pinError}
        onConfirm={handleConfirmPin}
      />
    </>
  );
}

/**
 * Reihenfolge erst Bestand buchen, dann Misch-EK (Plan 2026-10-19): der Server lehnt den
 * Confirm mit 409 ab, solange der Container nicht `eingelagert` ist und kein Bestands-Nachweis
 * je Kopf vorliegt (Bestand gebucht oder Final-Push). Der Dialog spiegelt genau diese Regel, damit
 * niemand fuer eine sichere Absage die PIN eingibt. Quelle ist die Akte (Status) bzw. der
 * Arbeitsplatz (Schritt 6 done = eingelagert, Schritt 7 done = Nachweis).
 */
function mischEkReihenfolgeOffen(order: PurchaseOrderFile, shipmentId: number | null): boolean {
  if (shipmentId === null) return false;
  const shipment = order.shipments.find((s) => s.id === shipmentId);
  if (!shipment) return false;
  // Der Server traegt den Grund als Code an Schritt 7 (Status eingelagert UND Bestands-
  // Nachweis noetig; der Status allein kann von Hand gesetzt sein). Liegt die Akte ohne
  // Arbeitsplatz vor, bleibt der Status die einzige Naeherung; der Server lehnt sonst mit 409 ab.
  const spur = order.arbeitsplatz?.spuren.find((s) => s.shipment_id === shipmentId);
  const s7 = spur?.schritte.find((x) => x.key === "einstandspreis");
  if (s7) return s7.status !== "done" && s7.codes.some((c) => c.code === "bestand_nicht_gebucht");
  return shipment.status !== "eingelagert";
}

function MischEkDialog({
  shipmentId,
  reihenfolgeOffen,
  onClose,
}: {
  shipmentId: number | null;
  reihenfolgeOffen: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useMischEkVorschau(shipmentId);
  const confirmMischEk = useConfirmMischEk(shipmentId ?? 0);

  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [korrekturChecked, setKorrekturChecked] = useState(false);
  const [results, setResults] = useState<ConfirmMischEkResultItem[] | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  // Plan 2026-10-19 (b): Alt-Menge je Kopf aenderbar; gelbe/rote Vorschlaege sichtbar bestaetigen.
  const [altGeaendert, setAltGeaendert] = useState<Record<number, number>>({});
  const [altBestaetigt, setAltBestaetigt] = useState<Record<number, boolean>>({});

  const isBlocked = data && "blocked" in data && data.blocked;
  const vorschauData = data && !("blocked" in data && data.blocked) ? data : null;
  const pflichtOffen = vorschauData !== null && altPflichtOffen(vorschauData.positionen, altBestaetigt, altGeaendert);

  const schliessen = () => {
    setResults(null); setAlreadyApplied(false); setKorrekturChecked(false);
    setAltGeaendert({}); setAltBestaetigt({});
    onClose();
  };

  const handleConfirmPin = async (pin: string) => {
    if (!shipmentId || !vorschauData) return;
    setPinError(null);
    try {
      const res = await confirmMischEk.mutateAsync({
        pin, korrektur: korrekturChecked,
        alt_bestaetigt: altBestaetigtBody(vorschauData.positionen, altBestaetigt, altGeaendert),
      });
      setResults(res.results);
      setPinOpen(false);
      const failed = res.results.filter((r) => !r.ok).length;
      if (failed === 0) toast.success("Misch-EK erfolgreich freigegeben");
      else toast.warning(`${failed} von ${res.results.length} Positionen fehlgeschlagen — Details in der Ergebnisliste`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        // NUR der already_applied-409 zeigt die Korrektur-Checkbox — 409 kommt vom
        // Backend auch fuer no_pin/kein_we/blocked/kurs_fehlt/preis_fehlt/
        // nichts_eingelagert. Kein Fach-Code in der Envelope (error[0].code = HTTP-
        // Zahl), daher Match auf den stabilen Backend-String (routes.ts nack 409
        // 'Misch-EK wurde bereits freigegeben (Korrektur-Freigabe erforderlich)').
        if (err.status === 409 && err.message.includes("bereits freigegeben")) {
          setAlreadyApplied(true);
          setPinError(err.message);
          return;
        }
        setPinError(err.message);
      } else {
        setPinError("Fehler beim Freigeben des Misch-EK");
      }
    }
  };

  return (
    <>
      <Dialog open={shipmentId !== null} onOpenChange={(open) => { if (!open) schliessen(); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Misch-EK berechnen & freigeben</DialogTitle>
            <DialogDescription>
              Gewichteter Einkaufs-Mischpreis je Artikel auf Basis von Landed-EK und Alt-Bestand.
            </DialogDescription>
          </DialogHeader>

          {reihenfolgeOffen ? (
            <div role="alert" className="my-2 rounded-md border border-warn/40 bg-warn-soft p-3 text-xs text-ink">
              <div className="mb-1 text-sm font-semibold">Bestand noch nicht gebucht</div>
              Für diesen Container fehlt der Bestands-Nachweis (Schritt 6 „Bestand buchen“, je eingelagerter Position).
              Erst Bestand buchen, dann Misch-EK — der Server lehnt die Freigabe vorher ab. Die Vorschau unten bleibt zum Nachschlagen.
            </div>
          ) : null}

          {isLoading ? (
            <div className="py-6 text-center text-sm text-ink-2">Misch-EK Vorschau laden…</div>
          ) : isError ? (
            <div className="py-6 text-center text-sm text-coral">Fehler beim Laden der Misch-EK Vorschau.</div>
          ) : isBlocked ? (
            <div className="space-y-3 my-2">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200">
                <div className="font-semibold text-sm mb-1">Kosten-Checkliste unvollständig</div>
                {data.grund === "kosten_fehlen" ? (
                  <div className="text-xs">
                    Für diesen Container ist noch kein Kostenposten angelegt (Spedition, Zoll). Posten im Container anlegen
                    oder vom Admin ohne weitere Kosten freigeben lassen.
                  </div>
                ) : (
                  <>
                    <div className="text-xs mb-2">Folgende Kosten-Posten sind noch im Status 'erwartet':</div>
                    <ul className="list-disc list-inside text-xs space-y-0.5">
                      {data.offeneKosten.map((c, i) => (
                        <li key={i}><span className="font-medium">{c.typ}</span>: {c.label ?? "—"}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={schliessen}>Schließen</Button>
              </DialogFooter>
            </div>
          ) : vorschauData ? (
            <div className="space-y-4">
              {vorschauData.kurs === null && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900 dark:border-red-700/50 dark:bg-red-950/20 dark:text-red-200">
                  Effektiver Kurs fehlt (keine vollbezahlten USD/EUR-Payments)
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-ink-2">
                      <th className="py-2 pr-3">Sternnummer</th>
                      <th className="py-2 pr-3 text-right">Eingelagert</th>
                      <th className="py-2 pr-3 text-right">EK $</th>
                      <th className="py-2 pr-3 text-right">Landed-EK €</th>
                      <th className="py-2 pr-3">Alt-Bestand × Alt-EK</th>
                      <th className="py-2 pr-3 text-right font-bold">Misch-EK €</th>
                      {results && <th className="py-2 pr-3">Ergebnis</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {vorschauData.positionen.map((pos) => {
                      const resItem = results?.find((r) => r.stockItemId === pos.stockItemId);
                      const alt = altGeaendert[pos.stockItemId] ?? pos.altBestand;
                      const mischEk = alt === pos.altBestand
                        ? pos.mischEkEur
                        : mischEkLokal({ altBestand: alt, altEkEur: pos.altEkEur, qty: pos.qty, landedEkEur: pos.landedEkEur });
                      const pflicht = pos.altPflicht === true;
                      const erledigt = !pflicht || altBestaetigt[pos.stockItemId] === true || alt !== pos.altBestand;
                      const mehrerePositionen = pos.orderItemIds.length > 1;
                      const rowClass = pos.altEkFehlt
                        ? "bg-amber-50/60 dark:bg-amber-950/20"
                        : "";
                      return (
                        <tr key={pos.stockItemId} className={`border-b border-line/50 ${rowClass}`}>
                          <td className="py-2 pr-3 font-medium">
                            <GrundartikelLink code={pos.merchantSku} />
                            <PositionenHinweis anzahl={pos.orderItemIds.length} />
                            {pos.altEkFehlt && (
                              <span
                                className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                                title={mischEkHinweis(pos.altEkFehlt)}
                              >
                                Kein Alt-EK
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right">{pos.qty.toLocaleString("de-DE")}{einheitZusatz(pos.einheit)}</td>
                          <td
                            className="py-2 pr-3 text-right text-xs"
                            title={mehrerePositionen ? "Ø-Preis der Positionen, gewichtet nach Bestellmenge" : undefined}
                          >
                            {formatUsdPreis(pos.unitPriceUsd, mehrerePositionen)}{jeEinheit(pos.einheit)}
                          </td>
                          <td className="py-2 pr-3 text-right">{formatEuroGenau(pos.landedEkEur)}{jeEinheit(pos.einheit)}</td>
                          <td className="py-2 pr-3 text-xs text-ink-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                aria-label={`Alt-Bestand ${pos.merchantSku ?? pos.stockItemId}`}
                                value={alt}
                                disabled={!!results || confirmMischEk.isPending}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  setAltGeaendert((d) => ({ ...d, [pos.stockItemId]: Number.isNaN(v) || v < 0 ? 0 : v }));
                                }}
                                className="h-7 w-24 text-right text-xs"
                              />
                              <span>{einheitZusatz(pos.einheit)} × {formatEuroGenau(pos.altEkEur)}</span>
                              {pos.altAmpel ? (
                                <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${AMPEL_KLASSE[pos.altAmpel]}`}>
                                  {AMPEL_TEXT[pos.altAmpel]}
                                </span>
                              ) : null}
                            </div>
                            {pos.altHerkunft ? (
                              <div className="mt-0.5">
                                {herkunftText(pos.altHerkunft)}
                                {pos.altHerkunft.gruende.length > 0 ? ` · ${pos.altHerkunft.gruende.map(grundText).join(" · ")}` : ""}
                              </div>
                            ) : pos.altQuelle === "fallback" ? (
                              <div className="mt-0.5">Ohne Buchungs-Nachweis: heutiger Bestand minus eingelagert</div>
                            ) : null}
                            {pflicht ? (
                              <label className={`mt-1 flex items-center gap-1.5 ${erledigt ? "text-ink-2" : "font-semibold text-coral"}`}>
                                <input
                                  type="checkbox"
                                  checked={erledigt}
                                  disabled={!!results || alt !== pos.altBestand}
                                  onChange={(e) => setAltBestaetigt((d) => ({ ...d, [pos.stockItemId]: e.target.checked }))}
                                />
                                Alt-Bestand geprüft
                              </label>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 text-right font-bold text-ink">
                            {formatEuroGenau(mischEk)}{jeEinheit(pos.einheit)}
                          </td>
                          {results && (
                            <td className="py-2 pr-3 text-xs">
                              {resItem ? (
                                <span className={resItem.ok ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                  {resItem.ok ? "✓ freigegeben" : `✗ ${resItem.error ?? "Fehler"}`}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-xs text-ink-2 border-t border-line/60 pt-2">
                <span>Effektiver Wechselkurs: <strong className="text-ink">{vorschauData.kurs ?? "—"}</strong></span>
              </div>

              {alreadyApplied && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/20 dark:text-amber-200 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="korrektur-check"
                    checked={korrekturChecked}
                    onChange={(e) => setKorrekturChecked(e.target.checked)}
                    className="rounded border-amber-400"
                  />
                  <label htmlFor="korrektur-check" className="font-medium cursor-pointer">
                    Misch-EK wurde bereits freigegeben — Erneut überschreiben (Korrektur-Freigabe)
                  </label>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={schliessen}>
                  {results ? "Schließen" : "Abbrechen"}
                </Button>
                {!results && (
                  <Button
                    size="sm"
                    title={pflichtOffen ? "Gelbe und rote Alt-Bestände erst prüfen oder ändern" : undefined}
                    disabled={confirmMischEk.isPending || reihenfolgeOffen || pflichtOffen || vorschauData.kurs === null || (alreadyApplied && !korrekturChecked)}
                    onClick={() => setPinOpen(true)}
                  >
                    Misch-EK freigeben…
                  </Button>
                )}
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <PinConfirmDialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        titel="Misch-EK freigeben"
        beschreibung="Bitte 6-stellige PIN eingeben, um den Misch-EK als Einkaufspreis zu übernehmen."
        busy={confirmMischEk.isPending}
        errorText={pinError}
        onConfirm={handleConfirmPin}
      />
    </>
  );
}
